// NC-ported manager-view preview component (3,987 LOC). The chart layers
// graft extra computed fields onto BE shapes (key/fill/fullName on the
// funnel; gamification sub-trees on the weekly recap), so dashboardData
// is read through a permissive Record<string, any> alias (LooseRow). That
// gives every downstream callback a typed parameter without forcing every
// nested shape to be enumerated. Tightening to concrete DTOs is a future
// epic — when it lands, narrow LooseRow and remove the `as unknown as
// FilterableEntity[]` casts that thread BE rows into the filter drawer.
'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Users,
  UserCheck,
  UserCog,
  TrendingUp,
  TrendingDown,
  Trophy,
  Award,
  Target,
  ShieldAlert,
  ExternalLink,
  AlertTriangle,
  Megaphone,
  ShieldCheck,
  RefreshCw,
  Clock,
  FileText,
  FileType,
  FileSpreadsheet,
  Download,
  Camera,
  Network,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  PartyPopper,
  X,
  Frown,
  GraduationCap,
  CheckCircle2,
  XCircle,
  ArrowUp,
  Bell,
  Filter,
  Search,
  ArrowUpDown,
  Mail,
  Eye,
  MousePointer,
  Shield,
  BookOpen,
  Circle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UnifiedDataTable } from '@/components/ui/unified-data-table';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AdvancedFilterDrawer } from '@/app/settings/portal/_components/manager/advanced-filter-drawer';
import type { FilterableEntity } from '@/lib/teams-portal/types';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { HierarchyTree } from '@/app/settings/portal/_components/manager/hierarchy-tree';
import { fetchPortalApi } from '@/lib/api/portal-fetch';
import { portalAdminFetch } from '@/lib/portal/api';
import { Skeleton } from '@/components/ui/skeleton';
import { getMonogram } from '@/lib/utils/get-monogram';
import {
  isWeeklyRecapDismissed,
  dismissWeeklyRecap as persistWeeklyRecapDismissal,
} from '@/lib/portal/weekly-recap-dismissal';

// Types
interface TeamMember {
  id: string;
  name: string;
  email: string;
  department: string;
  riskLevel: 'High' | 'Medium' | 'Low';
  totalPoints: number;
  badgeCount: number;
  badges: string[];
  rank: number;
  trend: number;
  lastActivity: string;
  isDirectReport: boolean;
  isManager: boolean;
  reportsTo?: string;
  timesCompromised?: number;
  trainingCompletion?: number;
  reportRate?: number;
  overdueRate?: number;
  avatar?: string;
}



// Extended TeamMember interface for My Team tab
interface MyTeamMember {
  id: string;
  name: string;
  email: string;
  avatar: string;
  department: string;
  designation: string;
  riskLevel: 'High' | 'Medium' | 'Low';
  riskScore: number;
  totalPoints: number;
  badgeCount: number;
  badges: string[];
  rank: number;
  rankInDepartment: number;
  rankInOrg: number;
  trend: number;
  lastActivity: string;
  isDirectReport: boolean;
  isIndirectReport: boolean;
  isManager: boolean;
  reportsTo?: string;
  managerName?: string;
  membersInTeam: number;
  timesCompromised: number;
  trainingCompletion: number;
  reportRate: number;
  phishing: {
    sent: number;
    opened: number;
    openedRate: number;
    clicked: number;
    clickedRate: number;
    compromised: number;
    compromisedRate: number;
    reported: number;
    reportedRate: number;
    trained: number;
    trainedRate: number;
    evaluated: number;
    evaluatedRate: number;
  };
  training: {
    assigned: number;
    completed: number;
    completedRate: number;
    inProgress: number;
    inProgressRate: number;
    trained: number;
    trainedRate: number;
    evaluated: number;
    evaluatedRate: number;
    expired: number;
    expiredRate: number;
  };
  announcement: {
    sent: number;
    opened: number;
    openedRate: number;
    acknowledged: number;
    acknowledgedRate: number;
    reported: number;
    reportedRate: number;
    trained: number;
    trainedRate: number;
    evaluated: number;
    evaluatedRate: number;
  };
}

const getRiskLevelColor = (level: string) => {
  switch (level) {
    case 'High':
      return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800';
    case 'Low':
      return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
    default:
      return 'bg-muted text-foreground border-border';
  }
};

// Helper function to compute risk factors for each employee
const getRiskFactors = (member: TeamMember): Array<{ label: string; color: string; colorClass: string }> => {
  const factors: Array<{ label: string; color: string; colorClass: string }> = [];
  
  // Training completion below 50%
  if ((member.trainingCompletion || 0) < 50) {
    factors.push({ label: 'Low Training', color: 'red', colorClass: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800' });
  }

  // Overdue rate above 30%
  if ((member.overdueRate || 0) > 30) {
    factors.push({ label: 'Overdue', color: 'orange', colorClass: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800' });
  }

  // Repeat compromises (2 or more)
  if ((member.timesCompromised || 0) >= 2) {
    factors.push({ label: 'Repeat Compromised', color: 'red', colorClass: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800' });
  }

  // Low report rate (below 30%)
  if ((member.reportRate || 0) < 30) {
    factors.push({ label: 'Low Reporting', color: 'yellow', colorClass: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800' });
  }

  // Negative trend (declining performance)
  if (member.trend < 0) {
    factors.push({ label: 'Declining', color: 'orange', colorClass: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800' });
  }
  
  return factors;
};


// Re-export the shared monogram helper under the local alias used by this
// component (kept for the existing call sites — `getInitials(name)` is the
// long-standing API throughout this 3,987-line NC port).
const getInitials = (name: string | null | undefined): string =>
  getMonogram(name);

// Helper function to generate a consistent background color based on name
const getAvatarColor = (name: string): string => {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
    'bg-cyan-500',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

// Helper component to render employee avatar
const EmployeeAvatar = ({ name, avatar, className }: { name: string; avatar?: string; className?: string }) => {
  return (
    <Avatar className={className}>
      {avatar && <AvatarImage src={avatar} alt={name} />}
      <AvatarFallback className={`${getAvatarColor(name)} text-white font-semibold`}>
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
};

interface ManagerDashboardContentProps {
  activeTab?: 'dashboard' | 'myteam';
  onTabChange?: (tab: 'dashboard' | 'myteam') => void;
  previewMode?: boolean;
  /**
   * If set, the component fetches dashboard data via the ADMIN endpoint
   * `/v1/team-portal/view-as/<managerId>` using the admin JWT (impersonation
   * preview at `/teams-portal/view-as/<id>`). When unset, the component
   * fetches via the PORTAL endpoint `/v1/portal/team/dashboard` using the
   * portal JWT (live employee `/portal` flow). The two endpoints return the
   * same shape; only the auth + scoping differ.
   */
  managerId?: string;
  // Dashboard section visibility
  showManagerProfile?: boolean;
  showDesignation?: boolean;
  showLeaderboardRank?: boolean;
  showTotalPoints?: boolean;
  showTeamStructure?: boolean;
  showDirectReports?: boolean;
  showIndirectReports?: boolean;
  showManagersUnder?: boolean;
  showHierarchyButton?: boolean;
  showPhishingPerformance?: boolean;
  showPhishingKPIs?: boolean;
  showPhishingFunnel?: boolean;
  showPhishingBreakdown?: boolean;
  showTrainingPerformance?: boolean;
  showTrainingKPIs?: boolean;
  showTopCompleters?: boolean;
  showNeedsImprovement?: boolean;
  showAnnouncementPerformance?: boolean;
  showAnnouncementKPIs?: boolean;
  showAnnouncementFunnel?: boolean;
  showGamification?: boolean;
  showLevels?: boolean;
  showBadgeDistribution?: boolean;
  showTeamLeaderboard?: boolean;
  // My Team sub-tab visibility
  showMyTeamLeaderboard?: boolean;
  showMyTeamRiskScore?: boolean;
  showMyTeamPhishing?: boolean;
  showMyTeamTraining?: boolean;
  showMyTeamAnnouncement?: boolean;
}

export function ManagerDashboardContent({
  activeTab, onTabChange, previewMode, managerId,
  showManagerProfile = true, showDesignation = true, showLeaderboardRank = true, showTotalPoints = true,
  showTeamStructure = true, showDirectReports = true, showIndirectReports = true, showManagersUnder = true, showHierarchyButton = true,
  showPhishingPerformance = true, showPhishingKPIs = true, showPhishingFunnel = true, showPhishingBreakdown = true,
  showTrainingPerformance = true, showTrainingKPIs = true, showTopCompleters = true, showNeedsImprovement = true,
  showAnnouncementPerformance = true, showAnnouncementKPIs = true, showAnnouncementFunnel = true,
  showGamification = true, showLevels = true, showBadgeDistribution = true, showTeamLeaderboard = true,
  showMyTeamLeaderboard = true, showMyTeamRiskScore = true, showMyTeamPhishing = true, showMyTeamTraining = true, showMyTeamAnnouncement = true,
}: ManagerDashboardContentProps) {
  const router = useRouter();

  // Dashboard data from API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  useEffect(() => {
    // Two contexts return the same 18-field TeamDashboardData shape:
    //   • Admin impersonation (`managerId` set): admin JWT -> /team-portal/view-as/<id>
    //   • Live employee (`managerId` undefined): portal JWT -> /portal/team/dashboard
    // Branching here keeps the rest of the component context-agnostic.
    const fetcher = managerId
      ? portalAdminFetch(`team-portal/view-as/${managerId}`)
      : fetchPortalApi('/portal/team/dashboard');
    fetcher
      .then((data) => {
        setDashboardData(data);
        setDashboardLoading(false);
      })
      .catch(() => setDashboardLoading(false));
  }, [managerId]);

  // Derive data from API response (wrapped in useMemo to avoid unstable references)
  const emptyManagerProfile = useMemo(() => ({ id: '', name: '', email: '', department: '', designation: '', rank: 0, totalPoints: 0, badges: [] as string[], avatar: '' }), []);
  const emptyPhishingMetrics = useMemo(() => ({ sent: 0, opened: 0, openedRate: 0, clicked: 0, clickedRate: 0, compromised: 0, compromisedRate: 0, reported: 0, reportedRate: 0, trained: 0, trainedRate: 0, evaluated: 0, evaluatedRate: 0 }), []);
  const emptyMyTeamManagerOfManager = useMemo(() => ({ id: '', name: '', email: '', designation: '' }), []);
  const emptyMyTeamManagerProfile = useMemo(() => ({ id: '', name: '', email: '', avatar: '', department: '', designation: '', reportsTo: '' }), []);
  const emptyGamificationHistory = useMemo(() => ({} as Record<string, Array<{ id: string; dateTime: string; type: 'badge' | 'points' | 'level'; name: string; event: string; amount: string; icon?: string }>>), []);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const emptyArray = useMemo(() => [] as any[], []);

  // Permissive shape lets downstream callbacks ((c) => c.x, (acc, row) => …)
  // resolve their parameter types instead of silently inferring `any`. The
  // file historically lived under `// @ts-nocheck` because the chart layers
  // tack on extra fields (key/fill/fullName) the BE doesn't return — using a
  // permissive Record keeps both worlds typed without forcing every nested
  // shape to be enumerated. Tightening to concrete DTOs is a follow-up epic.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type LooseRow = Record<string, any>;
  const managerProfile = dashboardData?.managerProfile ?? emptyManagerProfile;
  const allTeamMembers: TeamMember[] = dashboardData?.allTeamMembers ?? emptyArray;
  const phishingCampaigns: LooseRow[] =
    dashboardData?.phishingCampaigns ?? emptyArray;
  const trainingCourses: LooseRow[] =
    dashboardData?.trainingCourses ?? emptyArray;
  const announcementCampaigns: LooseRow[] =
    dashboardData?.announcementCampaigns ?? emptyArray;
  const phishingMetrics = dashboardData?.phishingMetrics ?? emptyPhishingMetrics;
  const myTeamManagerOfManager =
    dashboardData?.myTeamManagerOfManager ?? emptyMyTeamManagerOfManager;
  const myTeamManagerProfile =
    dashboardData?.myTeamManagerProfile ?? emptyMyTeamManagerProfile;
  // The BE returns members in `DashboardTeamMember` shape (flat fields only —
  // no nested phishing/training/announcement sub-objects). The FE's MyTeam
  // table reads `member.phishing.sent`, `member.training.assigned`, etc.
  // We backfill zero-defaults here so the table renders zeros instead of
  // crashing with "Cannot read properties of undefined (reading 'sent')".
  // Future epic: populate these aggregates per-member on the BE side.
  const emptyPhishingNested = useMemo(() => ({
    sent: 0, opened: 0, openedRate: 0,
    clicked: 0, clickedRate: 0,
    compromised: 0, compromisedRate: 0,
    reported: 0, reportedRate: 0,
    trained: 0, trainedRate: 0,
    evaluated: 0, evaluatedRate: 0,
  }), []);
  const emptyTrainingNested = useMemo(() => ({
    assigned: 0,
    completed: 0, completedRate: 0,
    inProgress: 0, inProgressRate: 0,
    notStarted: 0, notStartedRate: 0,
    trained: 0, trainedRate: 0,
    evaluated: 0, evaluatedRate: 0,
    expired: 0, expiredRate: 0,
    overdue: 0, overdueRate: 0,
  }), []);
  const emptyAnnouncementNested = useMemo(() => ({
    sent: 0, opened: 0, openedRate: 0,
    acknowledged: 0, acknowledgedRate: 0,
    reported: 0, reportedRate: 0,
    trained: 0, trainedRate: 0,
    evaluated: 0, evaluatedRate: 0,
  }), []);
  const myTeamAllMembers: MyTeamMember[] = useMemo(() => {
    const raw: LooseRow[] = dashboardData?.myTeamAllMembers ?? [];
    return raw.map((m) => ({
      ...m,
      phishing: { ...emptyPhishingNested, ...(m.phishing ?? {}) },
      training: { ...emptyTrainingNested, ...(m.training ?? {}) },
      announcement: { ...emptyAnnouncementNested, ...(m.announcement ?? {}) },
    })) as MyTeamMember[];
  }, [dashboardData?.myTeamAllMembers, emptyPhishingNested, emptyTrainingNested, emptyAnnouncementNested]);
  const myTeamPhishingCampaigns: LooseRow[] =
    dashboardData?.myTeamPhishingCampaigns ?? emptyArray;
  const myTeamTrainingCourses: LooseRow[] =
    dashboardData?.myTeamTrainingCourses ?? emptyArray;
  const myTeamAnnouncementCampaigns: LooseRow[] =
    dashboardData?.myTeamAnnouncementCampaigns ?? emptyArray;
  const pointsDistributionData: LooseRow[] =
    dashboardData?.pointsDistributionData ?? emptyArray;
  const funnelData: LooseRow[] = dashboardData?.funnelData ?? emptyArray;
  const badgeDistributionData: LooseRow[] =
    dashboardData?.badgeDistributionData ?? emptyArray;
  const recentAnnouncements: LooseRow[] =
    dashboardData?.recentAnnouncements ?? emptyArray;
  const employeeGamificationHistory =
    dashboardData?.employeeGamificationHistory ?? emptyGamificationHistory;
  // Defensive backfill: BE returns weeklyRecapData FLAT (weekStart, weekEnd,
  // ptsEarned, badgesEarned, phishingClicks, phishingReports, etc.), but the
  // FE modal reads deeply-nested paths like .gamification.isPositive,
  // .campaign.stats.sent, .training.completionRate. Without backfill those
  // crash with "Cannot read properties of undefined (reading 'X')". Future
  // epic: have the BE return the nested shape end-to-end.
  const weeklyRecapData: LooseRow | null = useMemo(() => {
    const raw = dashboardData?.weeklyRecapData;
    if (!raw) return null;
    return {
      ...raw,
      gamification: {
        isPositive: true,
        pointsGained: raw.ptsEarned ?? 0,
        previousPoints: 0,
        newBadges: [] as LooseRow[],
        levelUps: [] as LooseRow[],
        ...(raw.gamification ?? {}),
      },
      campaign: {
        isPositive: (raw.phishingClicks ?? 0) === 0,
        campaignName: '',
        runDate: '',
        totalParticipants: 0,
        stats: {
          sent: 0, opened: 0, openedRate: 0,
          clicked: raw.phishingClicks ?? 0, clickedRate: 0,
          compromised: 0, compromisedRate: 0,
          reported: raw.phishingReports ?? 0, reportedRate: 0,
        },
        compromisedEmployees: [] as LooseRow[],
        ...(raw.campaign ?? {}),
      },
      training: {
        totalAssigned: 0,
        completionRate: 0,
        previousRate: 0,
        completedThisWeek: [] as LooseRow[],
        ...(raw.training ?? {}),
      },
      announcement: {
        unacknowledged: [] as LooseRow[],
        ...(raw.announcement ?? {}),
      },
    };
  }, [dashboardData?.weeklyRecapData]);

  // Get unique departments from team members — strip W5 sentinel '' so Radix
  // <Select.Item> never receives an empty string value.
  const allDepartments = useMemo(() => [...new Set(allTeamMembers.map(m => m.department).filter(Boolean))] as string[], [allTeamMembers]);

  const [reporteeFilter, setReporteeFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [showSafest, setShowSafest] = useState(false);
  const [isHierarchyModalOpen, setIsHierarchyModalOpen] = useState(false);
  const [isEmployeeListModalOpen, setIsEmployeeListModalOpen] = useState(false);
  const [employeeListCategory, setEmployeeListCategory] = useState('all' as 'all' | 'direct' | 'indirect' | 'managers');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLevel, setSelectedLevel] = useState(null as number | null);
  const [selectedBadge, setSelectedBadge] = useState(null as { name: string; icon: string; count: number } | null);
  const [selectedLeaderboardEmployee, setSelectedLeaderboardEmployee] = useState(null as TeamMember | null);
  const [activityHistoryTab, setActivityHistoryTab] = useState('badges' as 'badges' | 'levels' | 'points');
  const [activityDurationFilter, setActivityDurationFilter] = useState('all' as 'all' | '7days' | '30days' | '90days');
  // The weekly recap modal opens at most once per ISO week per user.
  // Why: in preview mode (admin builder) the modal must stay closed so
  // admins aren't blocked from seeing the dashboard underneath; live mode
  // gates the auto-open on a per-week localStorage flag so the modal can't
  // become an every-visit annoyance.
  const [showWeeklyRecap, setShowWeeklyRecap] = useState(false);
  const [recapSlide, setRecapSlide] = useState(0);

  const hasWeeklyRecap = !!dashboardData?.weeklyRecapData;
  useEffect(() => {
    if (previewMode || !hasWeeklyRecap || isWeeklyRecapDismissed()) return;
    setShowWeeklyRecap(true);
  }, [previewMode, hasWeeklyRecap]);

  const handleDismissWeeklyRecap = () => {
    setShowWeeklyRecap(false);
    persistWeeklyRecapDismissal();
  };
  const [visibleBars, setVisibleBars] = useState(['sent', 'opened', 'clicked', 'compromised', 'reported', 'trained', 'evaluated'] as string[]);
  const [visibleAnnouncementBars, setVisibleAnnouncementBars] = useState(['sent', 'opened', 'acknowledged', 'trained'] as string[]);

  // Campaign and Training filters
  const [selectedPhishingCampaigns, setSelectedPhishingCampaigns] = useState([] as string[]);
  const [selectedTrainingCourses, setSelectedTrainingCourses] = useState([] as string[]);

  // Announcement filter
  const [selectedAnnouncementCampaigns, setSelectedAnnouncementCampaigns] = useState([] as string[]);

  // Advanced filter drawer states
  const [phishingDrawerOpen, setPhishingDrawerOpen] = useState(false);
  const [trainingDrawerOpen, setTrainingDrawerOpen] = useState(false);
  const [announcementDrawerOpen, setAnnouncementDrawerOpen] = useState(false);

  // Risk table filters, sorting, and pagination
  const [riskTableSearch, setRiskTableSearch] = useState('');
  const [riskLevelFilter, setRiskLevelFilter] = useState('all' as 'all' | 'High' | 'Medium' | 'Low');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [riskTableSort, setRiskTableSort] = useState({ key: 'riskLevel', direction: 'desc' as 'asc' | 'desc' });
  const [riskTablePage, setRiskTablePage] = useState(0);
  const [riskTablePageSize, setRiskTablePageSize] = useState(10);

  const itemsPerPage = 5;
  const totalRecapSlides = 4;

  // ========== MY TEAM TAB STATE ==========
  const [internalMainTab, setInternalMainTab] = useState('dashboard' as 'dashboard' | 'myteam');
  const mainTab = activeTab ?? internalMainTab;
  const setMainTab = (tab: 'dashboard' | 'myteam') => {
    setInternalMainTab(tab);
    onTabChange?.(tab);
  };
  const [myTeamSearchQuery, setMyTeamSearchQuery] = useState('');
  const [myTeamDepartmentFilter, setMyTeamDepartmentFilter] = useState('all');
  const [myTeamCurrentPage, setMyTeamCurrentPage] = useState(1);
  const [myTeamSelectedPhishingCampaigns, setMyTeamSelectedPhishingCampaigns] = useState([] as string[]);
  const [myTeamSelectedTrainingCourses, setMyTeamSelectedTrainingCourses] = useState([] as string[]);
  const [myTeamSelectedAnnouncementCampaigns, setMyTeamSelectedAnnouncementCampaigns] = useState([] as string[]);

  // My Team advanced filter drawer states
  const [myTeamPhishingDrawerOpen, setMyTeamPhishingDrawerOpen] = useState(false);
  const [myTeamTrainingDrawerOpen, setMyTeamTrainingDrawerOpen] = useState(false);
  const [myTeamAnnouncementDrawerOpen, setMyTeamAnnouncementDrawerOpen] = useState(false);

  const [myTeamActiveTab, setMyTeamActiveTab] = useState('leaderboard');
  // Sort + rows-per-page for the My Team tab (tickets 852/853). Only fields
  // actually present on the team-member rows are offered — Name, Last Activity
  // and Points — so the dropdown never sorts on a fabricated column.
  const [myTeamSort, setMyTeamSort] = useState<'name' | 'lastActivity' | 'points'>('lastActivity');
  const [myTeamItemsPerPage, setMyTeamItemsPerPage] = useState(10);

  // Team filter state for My Team tab
  type MyTeamFilterType = 'all' | 'direct' | 'indirect' | 'myteam';
  const [myTeamFilter, setMyTeamFilter] = useState('all' as MyTeamFilterType);

  // View As state for My Team tab
  const [viewAsManager, setViewAsManager] = useState(null as MyTeamMember | null);
  const [viewAsHistory, setViewAsHistory] = useState([] as MyTeamMember[]);

  // Phishing/Announcement relative filters for My Team tab
  const [myTeamPhishingRelativeFilter, setMyTeamPhishingRelativeFilter] = useState('opened' as 'opened' | 'sent');
  const [myTeamAnnouncementRelativeFilter, setMyTeamAnnouncementRelativeFilter] = useState('opened' as 'opened' | 'sent');
  // ========== END MY TEAM TAB STATE ==========

  // Sync filter selections when dashboard data loads
  useEffect(() => {
    if (dashboardData) {
      if (phishingCampaigns.length > 0 && selectedPhishingCampaigns.length === 0) {
        setSelectedPhishingCampaigns(phishingCampaigns.map((c) => String(c.id)));
      }
      if (trainingCourses.length > 0 && selectedTrainingCourses.length === 0) {
        setSelectedTrainingCourses(trainingCourses.map((t) => String(t.id)));
      }
      if (announcementCampaigns.length > 0 && selectedAnnouncementCampaigns.length === 0) {
        setSelectedAnnouncementCampaigns(announcementCampaigns.map((a) => String(a.id)));
      }
      if (myTeamPhishingCampaigns.length > 0 && myTeamSelectedPhishingCampaigns.length === 0) {
        setMyTeamSelectedPhishingCampaigns(myTeamPhishingCampaigns.map((c) => String(c.id)));
      }
      if (myTeamTrainingCourses.length > 0 && myTeamSelectedTrainingCourses.length === 0) {
        setMyTeamSelectedTrainingCourses(myTeamTrainingCourses.map((t) => String(t.id)));
      }
      if (myTeamAnnouncementCampaigns.length > 0 && myTeamSelectedAnnouncementCampaigns.length === 0) {
        setMyTeamSelectedAnnouncementCampaigns(myTeamAnnouncementCampaigns.map((a) => String(a.id)));
      }
    }
  }, [dashboardData]);

  // ========== MY TEAM TAB COMPUTED VALUES & HELPERS ==========
  
  // Current manager being viewed (either the logged-in manager or a viewAs target)
  const myTeamCurrentManager = viewAsManager ? {
    id: viewAsManager.id,
    name: viewAsManager.name,
    email: viewAsManager.email,
    avatar: viewAsManager.avatar,
    department: viewAsManager.department,
    designation: viewAsManager.designation,
    reportsTo: viewAsManager.reportsTo,
  } : myTeamManagerProfile;

  // Get the manager that the current manager reports to.
  // `myTeamManagerProfile.id` is the top-of-tree manager (the recipient who
  // owns this dashboard). When viewAsManager.reportsTo points to that id,
  // we return their profile directly; otherwise we look up the named
  // manager in the local list.
  const getMyTeamCurrentManagersManager = () => {
    if (viewAsManager) {
      if (viewAsManager.reportsTo === myTeamManagerProfile.id) {
        return myTeamManagerProfile;
      }
      const manager = myTeamAllMembers.find(m => m.id === viewAsManager.reportsTo);
      if (manager) {
        return { name: manager.name, designation: manager.designation };
      }
      return myTeamManagerOfManager;
    }
    return myTeamManagerOfManager;
  };

  // Get team members for the current view
  const myTeamCurrentTeamMembers = useMemo(() => {
    if (viewAsManager) {
      return myTeamAllMembers.filter(m => m.reportsTo === viewAsManager.id);
    }
    return myTeamAllMembers;
  }, [viewAsManager, myTeamAllMembers]);

  // No-op until the portal-side "view-as" lands. The target route
  // (/teams-portal/view-as/:id) was a dead link; admin-side view-as
  // lives under /settings/team-portal/*.
  const handleViewAs = (_member: MyTeamMember) => {
    // intentionally empty
  };

  // Handle going back to previous view
  const handleBackToMyTeam = () => {
    setViewAsManager(null);
    setViewAsHistory([]);
    setMyTeamCurrentPage(1);
    setMyTeamFilter('all');
  };

  // Get unique departments for My Team tab.
  // Filter out empty / null / undefined values: Radix <Select.Item> THROWS
  // when value="" (it uses the empty string as a special "clear selection"
  // signal). When a reportee has no department in the DB, including them
  // would crash the entire My Team tab with "A <Select.Item /> must have a
  // value prop that is not an empty string."
  const myTeamDepartments = useMemo(() => {
    const depts = new Set(
      myTeamAllMembers
        .map((m) => m.department)
        .filter((d): d is string => !!d && d.length > 0),
    );
    return Array.from(depts);
  }, [myTeamAllMembers]);

  // Calculate team filter counts based on current view.
  // Use the BE-computed `isDirectReport`/`isManager` flags (the same source the
  // Dashboard tab's Team Structure card uses) so both tabs reconcile. The old
  // logic compared `member.reportsTo` (an EMAIL — the DTO sets reportsTo =
  // managerEmail) against `myTeamCurrentManager.id` (a UUID); they never
  // matched, so `direct` was always 0 and everyone counted as indirect.
  // "Indirect" mirrors the Dashboard definition: any reportee that is not a
  // direct report (i.e. `!isDirectReport`).
  const myTeamFilterCounts = useMemo(() => ({
    all: myTeamCurrentTeamMembers.length,
    direct: myTeamCurrentTeamMembers.filter(m => m.isDirectReport).length,
    indirect: myTeamCurrentTeamMembers.filter(m => !m.isDirectReport).length,
    myteam: myTeamCurrentTeamMembers.filter(m => m.isManager).length,
  }), [myTeamCurrentTeamMembers]);

  // Filter members based on team type, search, and department
  const myTeamFilteredMembers = useMemo(() => {
    const filtered = myTeamCurrentTeamMembers.filter((member) => {
      if (myTeamFilter === 'direct' && !member.isDirectReport) return false;
      if (myTeamFilter === 'indirect' && member.isDirectReport) return false;
      if (myTeamFilter === 'myteam' && !member.isManager) return false;

      const q = myTeamSearchQuery.toLowerCase();
      const matchesSearch =
        myTeamSearchQuery === '' ||
        (member.name ?? '').toLowerCase().includes(q) ||
        (member.email ?? '').toLowerCase().includes(q) ||
        (member.department ?? '').toLowerCase().includes(q) ||
        (member.designation ?? '').toLowerCase().includes(q);

      const matchesDepartment =
        myTeamDepartmentFilter === 'all' || member.department === myTeamDepartmentFilter;

      return matchesSearch && matchesDepartment;
    });

    const sorted = [...filtered];
    if (myTeamSort === 'name') {
      sorted.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    } else if (myTeamSort === 'points') {
      sorted.sort((a, b) => b.totalPoints - a.totalPoints);
    } else {
      // lastActivity — most recent first; unparseable dates sink to the bottom.
      sorted.sort((a, b) => {
        const ta = Date.parse(a.lastActivity ?? '');
        const tb = Date.parse(b.lastActivity ?? '');
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      });
    }
    return sorted;
  }, [myTeamCurrentTeamMembers, myTeamFilter, myTeamSearchQuery, myTeamDepartmentFilter, myTeamSort]);

  // Pagination for My Team tab
  const myTeamActiveTabMembers = myTeamFilteredMembers;
  const myTeamTotalPages = Math.ceil(myTeamActiveTabMembers.length / myTeamItemsPerPage);
  const myTeamPaginatedMembers = myTeamActiveTabMembers.slice(
    (myTeamCurrentPage - 1) * myTeamItemsPerPage,
    myTeamCurrentPage * myTeamItemsPerPage
  );

  // Get hierarchy chain for a member
  const getHierarchyChain = (member: MyTeamMember): string[] => {
    const chain: string[] = [];
    if (member.reportsTo) {
      const manager = myTeamAllMembers.find(m => m.id === member.reportsTo);
      if (manager) {
        chain.push(myTeamManagerProfile.name);
        if (manager.reportsTo && manager.reportsTo !== myTeamManagerProfile.id) {
          const grandManager = myTeamAllMembers.find(m => m.id === manager.reportsTo);
          if (grandManager) {
            chain.push(grandManager.name);
          }
        }
        chain.push(manager.name);
      } else if (member.reportsTo === myTeamManagerProfile.id) {
        chain.push(myTeamManagerProfile.name);
      }
    }
    chain.push(member.name);
    return chain;
  };

  // Get truncated hierarchy display
  const getHierarchyDisplay = (member: MyTeamMember) => {
    const chain = getHierarchyChain(member);
    const fullHierarchy = chain.join(' → ');
    if (chain.length <= 2) {
      return { display: fullHierarchy, extra: 0, fullHierarchy };
    }
    const displayPart = chain.slice(0, 2).join(' → ');
    const extraCount = chain.length - 2;
    return { display: displayPart, extra: extraCount, fullHierarchy };
  };

  // Get risk score badge
  const getRiskScoreBadge = (riskLevel: string, riskScore: number) => {
    let bgColor = '';
    let textColor = '';
    if (riskLevel === 'Low') {
      bgColor = 'bg-green-100 dark:bg-green-950';
      textColor = 'text-green-700 dark:text-green-300';
    } else if (riskLevel === 'Medium') {
      bgColor = 'bg-yellow-100 dark:bg-yellow-950';
      textColor = 'text-yellow-700 dark:text-yellow-300';
    } else {
      bgColor = 'bg-red-100 dark:bg-red-950';
      textColor = 'text-red-700 dark:text-red-300';
    }
    return (
      <Badge variant="outline" className={`${bgColor} ${textColor} border-0 font-medium px-3 py-1`}>
        {riskScore} - {riskLevel}
      </Badge>
    );
  };

  // Hierarchy cell component with tooltip
  const HierarchyCell = ({ member }: { member: MyTeamMember }) => {
    const { display, extra, fullHierarchy } = getHierarchyDisplay(member);
    if (extra === 0) {
      return <span className="text-sm text-muted-foreground whitespace-nowrap">{display}</span>;
    }
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
              <span className="text-sm text-muted-foreground">{display}</span>
              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 font-medium shrink-0">
                +{extra}
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-sm">{fullHierarchy}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Navigate to employee profile
  const handleMyTeamRowClick = (memberId: string) => {
    router.push(`/user-portal/${memberId}`);
  };
  // ========== END MY TEAM TAB COMPUTED VALUES & HELPERS ==========

  // Auto-advance carousel
  useEffect(() => {
    if (!showWeeklyRecap) return;
    const timer = setInterval(() => {
      setRecapSlide((prev) => (prev + 1) % totalRecapSlides);
    }, 8000);
    return () => clearInterval(timer);
  }, [showWeeklyRecap]);

  // Filter team members based on the scope dropdown (reporteeFilter) AND the
  // department selector. Threading `departmentFilter` here makes every panel
  // derived from `filteredMembers` — the KPI cards, phishing/training/
  // announcement breakdown lists, Top/Vulnerable/Laggard buckets, leaderboard
  // and levels — respond to the department selector, so the Dashboard tab
  // filters consistently with the My Team tab (tickets 867, 1111).
  const filteredMembers = useMemo(() => {
    let scoped: TeamMember[];
    switch (reporteeFilter) {
      case 'direct':
        scoped = allTeamMembers.filter((m) => m.isDirectReport);
        break;
      case 'indirect':
        scoped = allTeamMembers.filter((m) => !m.isDirectReport);
        break;
      case 'managers':
        scoped = allTeamMembers.filter((m) => m.isManager);
        break;
      default:
        scoped = allTeamMembers;
    }
    if (departmentFilter !== 'all') {
      scoped = scoped.filter((m) => m.department === departmentFilter);
    }
    return scoped;
  }, [reporteeFilter, allTeamMembers, departmentFilter]);

  // Calculate stats. The Team Structure KPI cards (Total Reportees / Direct /
  // Indirect / Managers) are derived from `filteredMembers` so the scope
  // dropdown and department selector actually re-scope the top KPIs (tickets
  // 867, 1111) instead of always reporting the full `allTeamMembers` set.
  const stats = useMemo(() => {
    const direct = filteredMembers.filter((m) => m.isDirectReport).length;
    const managers = filteredMembers.filter((m) => m.isManager).length;
    const indirect = filteredMembers.filter((m) => !m.isDirectReport).length;
    const highRisk = filteredMembers.filter((m) => m.riskLevel === 'High').length;
    const mediumRisk = filteredMembers.filter((m) => m.riskLevel === 'Medium').length;
    const lowRisk = filteredMembers.filter((m) => m.riskLevel === 'Low').length;
    const totalPoints = filteredMembers.reduce((sum, m) => sum + m.totalPoints, 0);
    const avgPoints = filteredMembers.length > 0 ? Math.round(totalPoints / filteredMembers.length) : 0;
    const totalBadges = filteredMembers.reduce((sum, m) => sum + m.badgeCount, 0);
    const avgRank = filteredMembers.length > 0 ? Math.round(
      filteredMembers.reduce((sum, m) => sum + m.rank, 0) / filteredMembers.length
    ) : 0;

    return {
      total: filteredMembers.length,
      direct,
      indirect,
      managers,
      highRisk,
      mediumRisk,
      lowRisk,
      totalPoints,
      avgPoints,
      totalBadges,
      avgRank,
    };
  }, [filteredMembers]);

  // Calculate filtered phishing metrics based on selected campaigns
  const filteredPhishingMetrics = useMemo(() => {
    const selectedCampaigns = phishingCampaigns.filter(c => selectedPhishingCampaigns.includes(c.id));
    if (selectedCampaigns.length === 0) {
      return { sent: 0, opened: 0, openedRate: 0, clicked: 0, clickedRate: 0, compromised: 0, compromisedRate: 0, reported: 0, reportedRate: 0, trained: 0, trainedRate: 0, evaluated: 0, evaluatedRate: 0 };
    }
    
    const totals = selectedCampaigns.reduce((acc, campaign) => ({
      sent: acc.sent + campaign.sent,
      opened: acc.opened + campaign.opened,
      clicked: acc.clicked + campaign.clicked,
      compromised: acc.compromised + campaign.compromised,
      reported: acc.reported + campaign.reported,
      trained: acc.trained + campaign.trained,
      evaluated: acc.evaluated + campaign.evaluated,
    }), { sent: 0, opened: 0, clicked: 0, compromised: 0, reported: 0, trained: 0, evaluated: 0 });

    return {
      ...totals,
      openedRate: totals.sent > 0 ? Number(((totals.opened / totals.sent) * 100).toFixed(1)) : 0,
      clickedRate: totals.sent > 0 ? Number(((totals.clicked / totals.sent) * 100).toFixed(1)) : 0,
      compromisedRate: totals.sent > 0 ? Number(((totals.compromised / totals.sent) * 100).toFixed(1)) : 0,
      reportedRate: totals.sent > 0 ? Number(((totals.reported / totals.sent) * 100).toFixed(1)) : 0,
      trainedRate: totals.sent > 0 ? Number(((totals.trained / totals.sent) * 100).toFixed(1)) : 0,
      evaluatedRate: totals.sent > 0 ? Number(((totals.evaluated / totals.sent) * 100).toFixed(1)) : 0,
    };
  }, [selectedPhishingCampaigns, phishingCampaigns]);

  // Calculate filtered training metrics based on selected courses
  const filteredTrainingMetrics = useMemo(() => {
    const selectedCourses = trainingCourses.filter(t => selectedTrainingCourses.includes(t.id));
    if (selectedCourses.length === 0) {
      return { assigned: 0, completed: 0, completionRate: 0, inProgress: 0, notStarted: 0, overdue: 0 };
    }

    const totals = selectedCourses.reduce((acc, course) => ({
      assigned: acc.assigned + course.assigned,
      completed: acc.completed + course.completed,
      inProgress: acc.inProgress + course.inProgress,
      notStarted: acc.notStarted + course.notStarted,
      overdue: acc.overdue + course.overdue,
    }), { assigned: 0, completed: 0, inProgress: 0, notStarted: 0, overdue: 0 });

    return {
      ...totals,
      completionRate: totals.assigned > 0 ? Number(((totals.completed / totals.assigned) * 100).toFixed(1)) : 0,
    };
  }, [selectedTrainingCourses, trainingCourses]);

  // Create funnel data from filtered metrics
  const filteredFunnelData = useMemo(() => [
    { name: 'Sent', fullName: 'Sent', value: filteredPhishingMetrics.sent, fill: 'var(--muted-foreground)', key: 'sent' },
    { name: 'Opened', fullName: 'Opened', value: filteredPhishingMetrics.opened, fill: '#fbbf24', key: 'opened' },
    { name: 'Clicked', fullName: 'Clicked', value: filteredPhishingMetrics.clicked, fill: '#ef4444', key: 'clicked' },
    { name: 'Compromised', fullName: 'Compromised', value: filteredPhishingMetrics.compromised, fill: '#ef4444', key: 'compromised' },
    { name: 'Reported', fullName: 'Reported', value: filteredPhishingMetrics.reported, fill: '#22c55e', key: 'reported' },
    { name: 'Trained', fullName: 'Trained', value: filteredPhishingMetrics.trained, fill: '#3b82f6', key: 'trained' },
    { name: 'Evaluated', fullName: 'Evaluated', value: filteredPhishingMetrics.evaluated, fill: '#8b5cf6', key: 'evaluated' },
  ], [filteredPhishingMetrics]);

  // Calculate filtered announcement metrics based on selected campaigns
  const filteredAnnouncementMetrics = useMemo(() => {
    const selectedCampaigns = announcementCampaigns.filter(a => selectedAnnouncementCampaigns.includes(a.id));
    if (selectedCampaigns.length === 0) {
      return { sent: 0, opened: 0, openedRate: 0, acknowledged: 0, acknowledgedRate: 0, trained: 0, trainedRate: 0 };
    }

    const totals = selectedCampaigns.reduce((acc, campaign) => ({
      sent: acc.sent + campaign.sent,
      opened: acc.opened + campaign.opened,
      acknowledged: acc.acknowledged + campaign.acknowledged,
      trained: acc.trained + campaign.trained,
    }), { sent: 0, opened: 0, acknowledged: 0, trained: 0 });

    return {
      ...totals,
      openedRate: totals.sent > 0 ? Number(((totals.opened / totals.sent) * 100).toFixed(1)) : 0,
      acknowledgedRate: totals.sent > 0 ? Number(((totals.acknowledged / totals.sent) * 100).toFixed(1)) : 0,
      trainedRate: totals.sent > 0 ? Number(((totals.trained / totals.sent) * 100).toFixed(1)) : 0,
    };
  }, [selectedAnnouncementCampaigns, announcementCampaigns]);

  // Create funnel data for announcement metrics
  const announcementFunnelData = useMemo(() => [
    { name: 'Sent', fullName: 'Sent', value: filteredAnnouncementMetrics.sent, fill: 'var(--foreground)', key: 'sent' },
    { name: 'Opened', fullName: 'Opened', value: filteredAnnouncementMetrics.opened, fill: '#fbbf24', key: 'opened' },
    { name: 'Acknowledged', fullName: 'Acknowledged', value: filteredAnnouncementMetrics.acknowledged, fill: '#4ade80', key: 'acknowledged' },
    { name: 'Trained', fullName: 'Trained', value: filteredAnnouncementMetrics.trained, fill: '#3b82f6', key: 'trained' },
  ], [filteredAnnouncementMetrics]);

  // Risk distribution for pie chart
  const riskDistributionData = [
    { name: 'High Risk', value: stats.highRisk, color: '#ef4444' },
    { name: 'Medium Risk', value: stats.mediumRisk, color: '#f59e0b' },
    { name: 'Low Risk', value: stats.lowRisk, color: '#22c55e' },
  ];

  // High risk members
  const highRiskMembers = filteredMembers.filter((m) => m.riskLevel === 'High');

  // Safest members (Low risk, sorted by points desc)
  const safestMembers = filteredMembers
    .filter((m) => m.riskLevel === 'Low')
    .sort((a, b) => b.totalPoints - a.totalPoints)
    .slice(0, 10);

  // Mutually-exclusive risk classification (ticket 995): assign each member to
  // exactly one bucket in descending severity so the same person can't appear
  // in Repeat Offenders AND Vulnerable AND Laggards at once. A member already
  // claimed by a higher-severity bucket is removed before the next filter.
  const classifiedIds = new Set<string>();

  // Repeat offenders (compromised 2+ times) — highest severity.
  const repeatOffenders = filteredMembers
    .filter((m) => (m.timesCompromised || 0) >= 2)
    .sort((a, b) => (b.timesCompromised || 0) - (a.timesCompromised || 0));
  repeatOffenders.forEach((m) => classifiedIds.add(m.id));

  // Vulnerable members (report rate below 30%), excluding repeat offenders.
  const vulnerableMembers = filteredMembers
    .filter((m) => !classifiedIds.has(m.id) && (m.reportRate || 0) < 30)
    .sort((a, b) => (a.reportRate || 0) - (b.reportRate || 0));
  vulnerableMembers.forEach((m) => classifiedIds.add(m.id));

  // Laggards (training completion below 50%), excluding the above buckets.
  const laggardMembers = filteredMembers
    .filter((m) => !classifiedIds.has(m.id) && (m.trainingCompletion || 0) < 50)
    .sort((a, b) => (a.trainingCompletion || 0) - (b.trainingCompletion || 0));
  laggardMembers.forEach((m) => classifiedIds.add(m.id));

  // Top performers = Low-risk members not claimed by any risk bucket above,
  // ranked by report rate desc. Computed here (not inline) so it excludes
  // members already surfaced as at-risk. We also require a real report-rate
  // threshold (>= 30%) rather than relying only on `riskLevel === 'Low'`, so a
  // Low-risk employee with a 0% report rate is never surfaced as a top
  // performer (ticket 870).
  const TOP_PERFORMER_MIN_REPORT_RATE = 30;
  const topPerformers = filteredMembers
    .filter(
      (m) =>
        !classifiedIds.has(m.id) &&
        m.riskLevel === 'Low' &&
        (m.reportRate || 0) >= TOP_PERFORMER_MIN_REPORT_RATE,
    )
    .sort((a, b) => (b.reportRate || 0) - (a.reportRate || 0))
    .slice(0, 5);

  // Members to display in table based on toggle
  const displayedMembers = showSafest ? safestMembers : highRiskMembers;

  // Filtered, sorted, and paginated risk members
  const filteredRiskMembers = useMemo(() => {
    // Start with either high risk or low risk based on toggle
    let data = showSafest 
      ? filteredMembers.filter(m => m.riskLevel === 'Low')
      : filteredMembers.filter(m => m.riskLevel === 'High' || m.riskLevel === 'Medium');
    
    // Apply search filter
    if (riskTableSearch) {
      const query = riskTableSearch.toLowerCase();
      data = data.filter(m => 
        m.name.toLowerCase().includes(query) || 
        m.email.toLowerCase().includes(query)
      );
    }
    
    // Apply risk level filter (within the already filtered set)
    if (riskLevelFilter !== 'all') {
      data = data.filter(m => m.riskLevel === riskLevelFilter);
    }
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      data = data.filter(m => m.department === departmentFilter);
    }
    
    // Apply sorting
    data.sort((a, b) => {
      const { key, direction } = riskTableSort;
      let aVal: any, bVal: any;
      
      switch (key) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'riskLevel':
          const riskOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          aVal = riskOrder[a.riskLevel];
          bVal = riskOrder[b.riskLevel];
          break;
        case 'trainingCompletion':
          aVal = a.trainingCompletion || 0;
          bVal = b.trainingCompletion || 0;
          break;
        case 'reportRate':
          aVal = a.reportRate || 0;
          bVal = b.reportRate || 0;
          break;
        case 'timesCompromised':
          aVal = a.timesCompromised || 0;
          bVal = b.timesCompromised || 0;
          break;
        case 'badgeCount':
          aVal = a.badgeCount;
          bVal = b.badgeCount;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return data;
  }, [filteredMembers, showSafest, riskTableSearch, riskLevelFilter, departmentFilter, riskTableSort]);

  const riskTablePageCount = Math.ceil(filteredRiskMembers.length / riskTablePageSize);
  
  const paginatedRiskMembers = useMemo(() => {
    return filteredRiskMembers.slice(
      riskTablePage * riskTablePageSize,
      (riskTablePage + 1) * riskTablePageSize
    );
  }, [filteredRiskMembers, riskTablePage, riskTablePageSize]);

  // Handle sort click
  const handleRiskTableSort = (key: string) => {
    setRiskTableSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Levels data
  const levels = [
    { id: 1, name: 'Beginner', pointsRequired: 200, badge: '/levels/level-1.png' },
    { id: 2, name: 'Intermediate', pointsRequired: 400, badge: '/levels/level-2.png' },
    { id: 3, name: 'Advanced', pointsRequired: 800, badge: '/levels/level-3.png' },
    { id: 4, name: 'Expert', pointsRequired: 1200, badge: '/levels/level-4.png' },
    { id: 5, name: 'Master', pointsRequired: 1600, badge: '/levels/level-5.png' },
  ];

  // Calculate level based on points
  const calculateLevel = (points: number) => {
    if (points >= 1600) return 5;
    if (points >= 1200) return 4;
    if (points >= 800) return 3;
    if (points >= 400) return 2;
    if (points >= 200) return 1;
    return 1;
  };

  // Get level info (name and badge)
  const getLevelInfo = (points: number) => {
    const levelId = calculateLevel(points);
    return levels.find(l => l.id === levelId) || levels[0];
  };

  // Points-band label for a level tile. The bands must mirror `calculateLevel`,
  // whose thresholds are [400, 800, 1200, 1600] — so the lowest tier spans
  // 0–399 (it also holds 0-point users), not "200+ pts" (tickets 856/1090).
  // Each intermediate tier shows "<lower>–<upper> pts"; the top tier stays "<n>+ pts".
  const levelBandLabel = (level: { id: number }) => {
    const thresholds = [0, 400, 800, 1200, 1600];
    const lower = thresholds[level.id - 1];
    const upper = thresholds[level.id]; // undefined for the top tier
    return upper === undefined ? `${lower}+ pts` : `${lower}–${upper - 1} pts`;
  };

  // Handle legend click to toggle bar visibility
  const handleLegendClick = (key: string) => {
    setVisibleBars((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Handle announcement legend click to toggle bar visibility
  const handleAnnouncementLegendClick = (key: string) => {
    setVisibleAnnouncementBars((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Badge images for display. Points at the generated medallion set — the flat
  // /badges/*.png this used before were a different art style, and
  // completed-training.png does not exist (the file is .jpeg), so it rendered a
  // broken image here too.
  const badgeImages = [
    '/badges/medallions/first-steps.png',
    '/badges/medallions/quick-reporter.png',
    '/badges/medallions/phishing-spotter.png',
    '/badges/medallions/guardian.png',
    '/badges/medallions/scholar.png',
  ];

  // Get employees for modal based on category
  const getEmployeesForCategory = () => {
    switch (employeeListCategory) {
      case 'all':
        return allTeamMembers;
      case 'direct':
        return allTeamMembers.filter((m) => m.isDirectReport);
      case 'indirect':
        return allTeamMembers.filter((m) => !m.isDirectReport);
      case 'managers':
        return allTeamMembers.filter((m) => m.isManager);
      default:
        return allTeamMembers;
    }
  };

  // Handler to open employee list modal
  const handleOpenEmployeeList = (category: 'all' | 'direct' | 'indirect' | 'managers') => {
    setEmployeeListCategory(category);
    setIsEmployeeListModalOpen(true);
  };

  if (dashboardLoading) {
    return (
      <div className="flex-1 space-y-6 p-8 pt-6 bg-background">
        {/* Tabs + filter row */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28 rounded-lg" />
            <Skeleton className="h-9 w-28 rounded-lg" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-32 rounded-lg" />
            <Skeleton className="h-9 w-32 rounded-lg" />
          </div>
        </div>
        {/* Manager profile card */}
        <div className="rounded-xl border bg-card p-6 flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
        {/* KPI row — 4 cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border bg-card p-4 space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
        </div>
        {/* Two chart panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-xl border bg-card p-6 space-y-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-48 w-full" />
              <div className="flex gap-2 flex-wrap">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-14" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
        {/* Team leaderboard rows */}
        <div className="rounded-xl border bg-card p-6 space-y-3">
          <Skeleton className="h-5 w-36" />
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex-1 space-y-6 p-8 pt-6 bg-background">
            {/* Main Dashboard/My Team Tabs */}
            <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as 'dashboard' | 'myteam')} className="space-y-6">
              {/* Page Header with Tabs and Filters */}
              <div className="flex items-center justify-between">
                <TabsList aria-label="Dashboard and My Team" className="h-auto p-0 bg-transparent gap-1">
                  <TabsTrigger value="dashboard" className="px-4 py-2 text-sm font-medium rounded-lg data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/50">
                    Dashboard
                  </TabsTrigger>
                  <TabsTrigger value="myteam" className="px-4 py-2 text-sm font-medium rounded-lg data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/50">
                    My Team
                  </TabsTrigger>
                </TabsList>
                {mainTab === 'dashboard' && (
                <div className="flex items-center gap-3">
                <Select value={reporteeFilter} onValueChange={setReporteeFilter}>
                  <SelectTrigger className="w-[200px] text-foreground border-border">
                    <SelectValue placeholder="Filter by reportee type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team Members</SelectItem>
                    <SelectItem value="direct">Direct Reportees Only</SelectItem>
                    <SelectItem value="indirect">Indirect Reportees</SelectItem>
                    <SelectItem value="managers">Managers Under Me</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-[150px] text-foreground border-border">
                    <SelectValue placeholder="Time period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
                )}
            </div>

            {/* Dashboard Tab Content */}
            <TabsContent value="dashboard" className="m-0 space-y-6">
            {/* Manager Profile & Team Overview Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Manager Profile Card */}
              {showManagerProfile && (
              <Card>
                <CardHeader className="pb-6">
                  <CardTitle className="text-lg font-semibold">My Profile</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar name={managerProfile.name} avatar={managerProfile.avatar} className="h-20 w-20" />
                    <div>
                      <h3 className="font-semibold text-lg">{managerProfile.name}</h3>
                      <p className="text-sm text-muted-foreground">{managerProfile.email}</p>
                      {showDesignation && managerProfile.designation && (
                      <Badge variant="outline" className="mt-1">
                        {managerProfile.designation}
                      </Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    {showLeaderboardRank && (
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-center gap-1 page-heading">
                        <Trophy className="h-5 w-5 text-muted-foreground" />{managerProfile.rank >= 1 ? `#${managerProfile.rank}` : '—'}
                      </div>
                      <p className="text-xs text-muted-foreground">Leaderboard Rank</p>
                    </div>
                    )}
                    {showTotalPoints && (
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="page-heading text-foreground">{managerProfile.totalPoints}</div>
                      <p className="text-xs text-muted-foreground">Total Points</p>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Team Structure Card */}
              {showTeamStructure && (
              <Card>
                <CardHeader className="pb-6 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Team Structure</CardTitle>
                  {showHierarchyButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsHierarchyModalOpen(true)}
                    className="gap-2"
                  >
                    <Network className="h-4 w-4" />
                    Structure
                  </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => handleOpenEmployeeList('all')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <Users className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.total}</div>
                        <p className="text-xs text-muted-foreground">Total Reportees</p>
                      </div>
                    </div>
                    {showDirectReports && (
                    <div
                      onClick={() => handleOpenEmployeeList('direct')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <UserCheck className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.direct}</div>
                        <p className="text-xs text-muted-foreground">Direct Reports</p>
                      </div>
                    </div>
                    )}
                    {showManagersUnder && (
                    <div
                      onClick={() => handleOpenEmployeeList('managers')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <UserCog className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.managers}</div>
                        <p className="text-xs text-muted-foreground">Managers Under</p>
                      </div>
                    </div>
                    )}
                    {showIndirectReports && (
                    <div
                      onClick={() => handleOpenEmployeeList('indirect')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <Users className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.indirect}</div>
                        <p className="text-xs text-muted-foreground">Indirect Reports</p>
                      </div>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )}
            </div>

            {/* Phishing Campaign Performance Section */}
            {showPhishingPerformance && (
            <Card>
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    Phishing Campaign Performance
                  </CardTitle>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setPhishingDrawerOpen(true)}
                    >
                      <Filter className="h-4 w-4" />
                      Filter Campaigns
                      {selectedPhishingCampaigns.length < phishingCampaigns.length && (
                        <Badge variant="secondary" className="ml-1 px-1.5 py-0">
                          {selectedPhishingCampaigns.length}
                        </Badge>
                      )}
                    </Button>
                    <AdvancedFilterDrawer
                      open={phishingDrawerOpen}
                      onOpenChange={setPhishingDrawerOpen}
                      entityType="campaign"
                      entityLabel="Campaigns"
                      entityLabelSingular="Campaign"
                      items={phishingCampaigns as unknown as FilterableEntity[]}
                      selectedIds={selectedPhishingCampaigns}
                      onApply={setSelectedPhishingCampaigns}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* KPI Cards Row - S-O-C-C-R-T-E */}
                {showPhishingKPIs && (
                <div className="grid grid-cols-7 gap-3">
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-foreground flex items-center justify-center">
                        <span className="text-lg font-bold text-background">S</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.sent}</div>
                    <p className="text-xs text-muted-foreground">Sent</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">O</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.opened}</div>
                    <p className="text-xs text-muted-foreground">{filteredPhishingMetrics.openedRate}% Opened</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">C</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.clicked}</div>
                    <p className="text-xs text-muted-foreground">{filteredPhishingMetrics.clickedRate}% Clicked</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">C</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.compromised}</div>
                    <p className="text-xs text-muted-foreground">{filteredPhishingMetrics.compromisedRate}% Compromised</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">R</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.reported}</div>
                    <p className="text-xs text-muted-foreground">{filteredPhishingMetrics.reportedRate}% Reported</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">T</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.trained}</div>
                    <p className="text-xs text-muted-foreground">{filteredPhishingMetrics.trainedRate}% Trained</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">E</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.evaluated}</div>
                    <p className="text-xs text-muted-foreground">{filteredPhishingMetrics.evaluatedRate}% Evaluated</p>
                  </div>
                </div>
                )}

                {/* Campaign Funnel - Full Width */}
                {showPhishingFunnel && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">Campaign Funnel</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={filteredFunnelData.filter(item => visibleBars.includes(item.key))}
                      layout="horizontal"
                      margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                        axisLine={{ stroke: 'var(--border)' }}
                      />
                      <YAxis tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} axisLine={{ stroke: 'var(--border)' }} />
                      <RechartsTooltip
                        formatter={(value, name, props) => [value, props.payload.fullName]}
                        contentStyle={{ fontSize: '12px', borderRadius: '6px', backgroundColor: 'var(--card)', color: 'var(--card-foreground)', border: '1px solid var(--border)' }}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {filteredFunnelData.filter(item => visibleBars.includes(item.key)).map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Custom Interactive Legend */}
                  <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                    {funnelData.map((item) => (
                      <div
                        key={item.key}
                        onClick={() => handleLegendClick(item.key)}
                        className={`flex items-center gap-2 cursor-pointer transition-opacity ${
                          visibleBars.includes(item.key) ? 'opacity-100' : 'opacity-40'
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-xs font-medium text-foreground">
                          {item.fullName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Performance - Top performers */}
                {showPhishingBreakdown && (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    {/* Column 1: Top Performers */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">Top Performers</h4>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                              <span className="text-xs text-muted-foreground font-semibold">i</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">Based on risk level, report rate, and security awareness scores</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>

                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {topPerformers.length > 0 ? (
                          topPerformers.map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <EmployeeAvatar name={member.name} avatar={member.avatar} className="h-8 w-8 text-xs" />
                                <span className="text-sm font-medium truncate">{member.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span><span className="font-medium text-green-600">{member.reportRate}%</span> report rate</span>
                                <span><span className="font-medium text-green-600">{member.timesCompromised || 0}</span> compromised</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">No top performers</p>
                        )}
                      </div>
                    </div>

                    {/* Column 2: Repeat Offenders */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">Repeat Offenders</h4>
                        <div className="flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                                <span className="text-xs text-muted-foreground font-semibold">i</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">Users compromised 2+ times in phishing campaigns</p>
                            </TooltipContent>
                          </Tooltip>
                          <Badge variant="outline" className="text-xs">{repeatOffenders.length}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {repeatOffenders.length > 0 ? (
                          repeatOffenders.slice(0, 5).map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <EmployeeAvatar name={member.name} avatar={member.avatar} className="h-8 w-8 text-xs" />
                                <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                                <Badge variant="destructive" className="text-xs px-1.5 py-0">
                                  {member.timesCompromised}x
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>Compromised <span className="font-medium text-red-600">{member.timesCompromised}x</span></span>
                                <span>Training: <span className="font-medium">{member.trainingCompletion}%</span></span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">No repeat offenders</p>
                        )}
                      </div>
                    </div>

                    {/* Column 3: Vulnerable */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">Vulnerable</h4>
                        <div className="flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                                <span className="text-xs text-muted-foreground font-semibold">i</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">Users with report rate below 30% - high risk of falling for attacks</p>
                            </TooltipContent>
                          </Tooltip>
                          <Badge variant="outline" className="text-xs">{vulnerableMembers.length}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {vulnerableMembers.length > 0 ? (
                          vulnerableMembers.slice(0, 5).map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-xs font-semibold`}>
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>Report rate: <span className="font-medium text-orange-600">{member.reportRate}%</span></span>
                                <span>Compromised: <span className="font-medium">{member.timesCompromised || 0}x</span></span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">No vulnerable members</p>
                        )}
                      </div>
                    </div>

                    {/* Column 4: Laggards */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">Laggards</h4>
                        <div className="flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                                <span className="text-xs text-muted-foreground font-semibold">i</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">Users with training completion below 50% - lacking security awareness</p>
                            </TooltipContent>
                          </Tooltip>
                          <Badge variant="outline" className="text-xs">{laggardMembers.length}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {laggardMembers.length > 0 ? (
                          laggardMembers.slice(0, 5).map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-xs font-semibold`}>
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>Training: <span className="font-medium text-amber-600">{member.trainingCompletion}%</span></span>
                                <span>Overdue: <span className="font-medium text-red-600">{member.overdueRate}%</span></span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">No laggards</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Training Performance Section */}
            {showTrainingPerformance && (
            <Card>
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    Training Performance
                  </CardTitle>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setTrainingDrawerOpen(true)}
                    >
                      <Filter className="h-4 w-4" />
                      Filter Training
                      {selectedTrainingCourses.length < trainingCourses.length && (
                        <Badge variant="secondary" className="ml-1 px-1.5 py-0">
                          {selectedTrainingCourses.length}
                        </Badge>
                      )}
                    </Button>
                    <AdvancedFilterDrawer
                      open={trainingDrawerOpen}
                      onOpenChange={setTrainingDrawerOpen}
                      entityType="training"
                      entityLabel="Training Courses"
                      entityLabelSingular="Training Course"
                      items={trainingCourses as unknown as FilterableEntity[]}
                      selectedIds={selectedTrainingCourses}
                      onApply={setSelectedTrainingCourses}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* KPI Cards Row */}
                {showTrainingKPIs && (
                <div className="grid grid-cols-5 gap-3">
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.assigned}</div>
                    <p className="text-xs font-medium text-muted-foreground">Total Assigned</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.completed} <span className="text-sm font-normal">({filteredTrainingMetrics.completionRate}%)</span></div>
                    <p className="text-xs font-medium text-muted-foreground">Completed</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.notStarted}</div>
                    <p className="text-xs font-medium text-muted-foreground">Not Started</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.inProgress}</div>
                    <p className="text-xs font-medium text-muted-foreground">In Progress</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.overdue}</div>
                    <p className="text-xs font-medium text-muted-foreground">Overdue</p>
                  </div>
                </div>
                )}

                {/* Training Completion by Member */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Top Completers */}
                  {showTopCompleters && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">
                      Top Completers
                    </h4>
                    <div className="space-y-2">
                      {[...filteredMembers]
                        .sort((a, b) => (b.trainingCompletion || 0) - (a.trainingCompletion || 0))
                        .slice(0, 3)
                        .map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                  {getInitials(member.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">{member.name}</p>
                                <p className="text-xs text-muted-foreground">{member.department}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600">{member.trainingCompletion || 0}%</p>
                              <p className="text-xs text-muted-foreground">Completed</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  )}

                  {/* Needs Improvement */}
                  {showNeedsImprovement && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">
                      Needs Improvement
                    </h4>
                    <div className="space-y-2">
                      {[...filteredMembers]
                        .sort((a, b) => (a.trainingCompletion || 0) - (b.trainingCompletion || 0))
                        .slice(0, 3)
                        .map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                  {getInitials(member.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">{member.name}</p>
                                <p className="text-xs text-muted-foreground">{member.department}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-red-600">{member.trainingCompletion || 0}%</p>
                              <p className="text-xs text-muted-foreground">Completed</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  )}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Announcement Campaign Performance Section */}
            {showAnnouncementPerformance && (
            <Card>
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    Announcement Campaign Performance
                  </CardTitle>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setAnnouncementDrawerOpen(true)}
                    >
                      <Filter className="h-4 w-4" />
                      Filter Announcements
                      {selectedAnnouncementCampaigns.length < announcementCampaigns.length && (
                        <Badge variant="secondary" className="ml-1 px-1.5 py-0">
                          {selectedAnnouncementCampaigns.length}
                        </Badge>
                      )}
                    </Button>
                    <AdvancedFilterDrawer
                      open={announcementDrawerOpen}
                      onOpenChange={setAnnouncementDrawerOpen}
                      entityType="announcement"
                      entityLabel="Announcements"
                      entityLabelSingular="Announcement"
                      items={announcementCampaigns as unknown as FilterableEntity[]}
                      selectedIds={selectedAnnouncementCampaigns}
                      onApply={setSelectedAnnouncementCampaigns}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* KPI Cards Row */}
                {showAnnouncementKPIs && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.sent}</div>
                    <p className="text-xs text-muted-foreground">Announcements Sent</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.opened} <span className="text-sm font-normal">({filteredAnnouncementMetrics.openedRate}%)</span></div>
                    <p className="text-xs text-muted-foreground">Opened</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.acknowledged} <span className="text-sm font-normal">({filteredAnnouncementMetrics.acknowledgedRate}%)</span></div>
                    <p className="text-xs text-muted-foreground">Acknowledged</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.trained} <span className="text-sm font-normal">({filteredAnnouncementMetrics.trainedRate}%)</span></div>
                    <p className="text-xs text-muted-foreground">Training Complete</p>
                  </div>
                </div>
                )}

                {/* Announcement Funnel Chart */}
                {showAnnouncementFunnel && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">Announcement Funnel</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={announcementFunnelData.filter(item => visibleAnnouncementBars.includes(item.key))}
                      layout="horizontal"
                      margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                        axisLine={{ stroke: 'var(--border)' }}
                      />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={{ stroke: 'var(--border)' }} />
                      <RechartsTooltip
                        formatter={(value, name, props) => [value, props.payload.fullName]}
                        contentStyle={{ fontSize: '12px', borderRadius: '6px', backgroundColor: 'var(--card)', color: 'var(--card-foreground)', border: '1px solid var(--border)' }}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {announcementFunnelData.filter(item => visibleAnnouncementBars.includes(item.key)).map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Custom Interactive Legend */}
                  <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                    {announcementFunnelData.map((item) => (
                      <div
                        key={item.key}
                        onClick={() => handleAnnouncementLegendClick(item.key)}
                        className={`flex items-center gap-2 cursor-pointer transition-opacity ${
                          visibleAnnouncementBars.includes(item.key) ? 'opacity-100' : 'opacity-40'
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-xs font-medium text-foreground">
                          {item.fullName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Gamification Overview - Consolidated Section */}
            {showGamification && (
            <Card>
              <CardHeader className="pb-6">
                <CardTitle className="text-lg font-semibold">
                  Gamification Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Levels Section */}
                {showLevels && (
                <div className="grid grid-cols-5 gap-3">
                  {levels.map((level) => {
                    const employeesAtLevel = filteredMembers.filter(m => calculateLevel(m.totalPoints) === level.id).length;
                    const levelColors = [
                      { bg: 'bg-muted/50', border: 'border-border', text: 'text-foreground', hover: 'hover:border-border' },
                      { bg: 'bg-yellow-50 dark:bg-yellow-950', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300', hover: 'hover:border-yellow-300 dark:hover:border-yellow-600' },
                      { bg: 'bg-orange-50 dark:bg-orange-950', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-300', hover: 'hover:border-orange-300 dark:hover:border-orange-600' },
                      { bg: 'bg-teal-50 dark:bg-teal-950', border: 'border-teal-200 dark:border-teal-800', text: 'text-teal-700 dark:text-teal-300', hover: 'hover:border-teal-300 dark:hover:border-teal-600' },
                      { bg: 'bg-purple-50 dark:bg-purple-950', border: 'border-purple-200 dark:border-purple-800', text: 'text-purple-700 dark:text-purple-300', hover: 'hover:border-purple-300 dark:hover:border-purple-600' },
                    ];
                    const colors = levelColors[level.id - 1];
                    return (
                      <div
                        key={level.id}
                        onClick={() => setSelectedLevel(level.id)}
                        className={`p-4 ${colors.bg} rounded-xl border ${colors.border} ${colors.hover} text-center space-y-3 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all`}
                      >
                        <div className="flex justify-center">
                          <img
                            src={level.badge}
                            alt={level.name}
                            className="w-14 h-14 object-contain"
                          />
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${colors.text}`}>{level.name}</p>
                          <p className="text-xs text-muted-foreground">{levelBandLabel(level)}</p>
                        </div>
                        <p className={`page-heading ${colors.text}`}>
                          {employeesAtLevel}
                        </p>
                        <p className="text-xs text-muted-foreground">employees</p>
                      </div>
                    );
                  })}
                </div>
                )}

                {/* Badge Distribution Section */}
                {showBadgeDistribution && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">Badge Distribution</h3>
                  <div className="grid grid-cols-3 gap-3 max-h-[320px] overflow-y-auto">
                    {badgeDistributionData.map((badge, index) => {
                      const totalEmployees = filteredMembers.length;
                      const percentage = Math.round((badge.count / totalEmployees) * 100);
                      const hasEmployees = badge.count > 0;
                      return (
                        <div
                          key={index}
                          onClick={() => setSelectedBadge({ name: badge.name, icon: badge.icon, count: badge.count })}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            hasEmployees
                              ? 'bg-muted/20 border-border hover:shadow-md hover:border-primary/30'
                              : 'bg-muted/5 border-border/50 opacity-60'
                          }`}
                        >
                          <img
                            src={badge.icon}
                            alt={badge.name}
                            className={`w-9 h-9 object-contain ${!hasEmployees ? 'grayscale' : ''}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${!hasEmployees ? 'text-muted-foreground' : ''}`}>
                              {badge.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={percentage} className="h-1.5 flex-1" />
                              <span className={`text-xs whitespace-nowrap ${!hasEmployees ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                                {badge.count}/{totalEmployees} ({percentage}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Team Leaderboard Section */}
                {showTeamLeaderboard && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">Team Leaderboard</h3>
                  {/* Header Row */}
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-xs font-medium text-muted-foreground w-8">Rank</span>
                      <span className="text-xs font-medium text-muted-foreground">Employee</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-medium text-muted-foreground w-36 text-center">Level</span>
                      <span className="text-xs font-medium text-muted-foreground w-32 text-center">Points</span>
                      <span className="text-xs font-medium text-muted-foreground w-32 text-center">Badges</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[...filteredMembers]
                      .sort((a, b) => a.rank - b.rank)
                      .slice(0, 10)
                      .map((member) => {
                        const memberLevel = getLevelInfo(member.totalPoints);
                        return (
                          <div
                            key={member.id}
                            onClick={() => setSelectedLeaderboardEmployee(member)}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 hover:border-primary/30 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-sm font-bold w-8">#{member.rank}</span>
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                  {getInitials(member.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <span className="text-sm font-medium">{member.name}</span>
                                <p className="text-xs text-muted-foreground">{member.department}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {/* Level Column - Wider Width */}
                              <div className="w-36 flex justify-center">
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-background rounded-lg border border-border">
                                  <img 
                                    src={memberLevel.badge} 
                                    alt={memberLevel.name}
                                    className="w-6 h-6 object-contain"
                                  />
                                  <span className="text-xs font-medium">{memberLevel.name}</span>
                                </div>
                              </div>
                              {/* Points Column - Wider Width */}
                              <div className="w-32 flex justify-center">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg border border-border">
                                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  <span className="text-xs font-semibold">{member.totalPoints}</span>
                                </div>
                              </div>
                              {/* Badges Column - Wider Width */}
                              <div className="w-32 flex justify-center">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg border border-border">
                                  <div className="flex -space-x-1">
                                    {badgeImages.slice(0, Math.min(member.badgeCount, 3)).map((src, i) => (
                                      <img 
                                        key={i} 
                                        src={src} 
                                        alt={`Badge ${i + 1}`} 
                                        className="w-5 h-5 rounded-full ring-1 ring-white object-cover" 
                                      />
                                    ))}
                                  </div>
                                  <span className="text-xs font-medium">{member.badgeCount}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Level Employees Modal */}
            <Dialog open={selectedLevel !== null} onOpenChange={(open) => !open && setSelectedLevel(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {selectedLevel && (
                      <>
                        <img
                          src={levels.find(l => l.id === selectedLevel)?.badge}
                          alt={levels.find(l => l.id === selectedLevel)?.name}
                          className="w-8 h-8 object-contain"
                        />
                        {levels.find(l => l.id === selectedLevel)?.name} Level Employees
                      </>
                    )}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Team members who have reached this gamification level
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selectedLevel && filteredMembers
                    .filter(m => calculateLevel(m.totalPoints) === selectedLevel)
                    .map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.department}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">{member.totalPoints} pts</p>
                          <p className="text-xs text-muted-foreground">Rank #{member.rank}</p>
                        </div>
                      </div>
                    ))}
                  {selectedLevel && filteredMembers.filter(m => calculateLevel(m.totalPoints) === selectedLevel).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No employees at this level</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Badge Employees Modal */}
            <Dialog open={selectedBadge !== null} onOpenChange={(open) => !open && setSelectedBadge(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {selectedBadge && (
                      <>
                        <img
                          src={selectedBadge.icon}
                          alt={selectedBadge.name}
                          className="w-8 h-8 object-contain"
                        />
                        {selectedBadge.name}
                      </>
                    )}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Team members who have earned this badge
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selectedBadge && filteredMembers
                    .slice(0, selectedBadge.count)
                    .map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.department}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">{member.totalPoints} pts</p>
                          <p className="text-xs text-muted-foreground">Rank #{member.rank}</p>
                        </div>
                      </div>
                    ))}
                  {selectedBadge && selectedBadge.count === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No employees have this badge yet</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Employee Gamification Modal */}
            <Dialog open={selectedLeaderboardEmployee !== null} onOpenChange={(open) => {
              if (!open) {
                setSelectedLeaderboardEmployee(null);
                setActivityHistoryTab('badges');
                setActivityDurationFilter('all');
              }
            }}>
              <DialogContent className="max-w-[80vw] max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader className="pb-4">
                  <DialogTitle>
                    {selectedLeaderboardEmployee && (
                      <div className="flex items-center justify-between">
                        {/* Employee Info */}
                        <div className="flex items-center gap-3">
                          <Avatar className="h-14 w-14">
                            <AvatarFallback className={`${getAvatarColor(selectedLeaderboardEmployee.name)} text-white text-lg font-semibold`}>
                              {getInitials(selectedLeaderboardEmployee.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col gap-1">
                            <p className="text-xl font-semibold">{selectedLeaderboardEmployee.name}</p>
                            <p className="text-sm text-muted-foreground font-normal">{selectedLeaderboardEmployee.email}</p>
                          </div>
                        </div>
                        
                        {/* Quick Stats */}
                        <div className="flex items-center gap-8">
                          <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg">
                            <img 
                              src={getLevelInfo(selectedLeaderboardEmployee.totalPoints).badge}
                              alt="Level"
                              className="w-10 h-10"
                            />
                            <div>
                              <p className="text-base font-semibold text-foreground">{getLevelInfo(selectedLeaderboardEmployee.totalPoints).name}</p>
                              <p className="text-xs text-muted-foreground">Current Level</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-base font-semibold text-foreground">{selectedLeaderboardEmployee.totalPoints}</p>
                              <p className="text-xs text-muted-foreground">Total Points</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg">
                            <div className="flex -space-x-2">
                              {badgeDistributionData.slice(0, Math.min(3, selectedLeaderboardEmployee.badgeCount)).map((badge, i) => (
                                <img key={i} src={badge.icon} alt={badge.name} className="w-8 h-8 rounded-full ring-2 ring-white" />
                              ))}
                            </div>
                            <div>
                              <p className="text-base font-semibold text-foreground">{selectedLeaderboardEmployee.badgeCount}</p>
                              <p className="text-xs text-muted-foreground">Badges Earned</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Gamification details, badges, and activity history for this employee
                  </DialogDescription>
                </DialogHeader>

                {selectedLeaderboardEmployee && (
                  <div className="flex-1 overflow-y-auto space-y-6 py-4">
                    {/* Badges Earned Section */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold">Badges Earned ({selectedLeaderboardEmployee.badgeCount})</h3>
                      {selectedLeaderboardEmployee.badgeCount > 0 ? (
                        <div className="grid grid-cols-6 gap-4">
                          {(() => {
                            const history = employeeGamificationHistory[selectedLeaderboardEmployee.id] || [];
                            const badgeHistory = history.filter((h: { type: string }) => h.type === 'badge');
                            return badgeDistributionData.slice(0, selectedLeaderboardEmployee.badgeCount).map((badge, index) => {
                              const historyEntry = badgeHistory[index];
                              return (
                                <div key={index} className="p-4 bg-card rounded-lg border border-border hover:border-primary/30 hover:shadow-sm transition-all">
                                  <div className="flex flex-col items-center gap-2">
                                    <img 
                                      src={badge.icon}
                                      alt={badge.name}
                                      className="w-14 h-14 object-contain"
                                    />
                                    <p className="text-sm font-medium text-center">{badge.name}</p>
                                    {historyEntry && (
                                      <div className="w-full px-2 py-1.5 bg-muted/50 rounded-md">
                                        <p className="text-xs text-muted-foreground text-center line-clamp-2">{historyEntry.event}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-6">No badges earned yet</p>
                      )}
                    </div>

                    {/* Activity History Section with Tabs */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold">Activity History</h3>
                        <div className="flex items-center gap-2">
                          <Button
                            variant={activityHistoryTab === 'badges' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActivityHistoryTab('badges')}
                            className="h-7 px-3 text-xs"
                          >
                            Badges
                          </Button>
                          <Button
                            variant={activityHistoryTab === 'levels' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActivityHistoryTab('levels')}
                            className="h-7 px-3 text-xs"
                          >
                            Levels
                          </Button>
                          <Button
                            variant={activityHistoryTab === 'points' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActivityHistoryTab('points')}
                            className="h-7 px-3 text-xs"
                          >
                            Points
                          </Button>
                        </div>
                      </div>
                      
                      {/* Activity history — UnifiedDataTable (replaces bare <table>).
                          Columns vary by activityHistoryTab (badges/levels/points).
                          Data comes from local employeeGamificationHistory (preview
                          NC copy — no server pagination needed). */}
                      {(() => {
                        const history = employeeGamificationHistory[selectedLeaderboardEmployee.id] || [];
                        const filteredEntries = history.filter((e: { type: string }) =>
                          activityHistoryTab === 'badges' ? e.type === 'badge' :
                          activityHistoryTab === 'levels' ? e.type === 'level' :
                          e.type === 'points'
                        );

                        const badgeColumns = [
                          {
                            id: 'id',
                            accessorKey: 'id',
                            header: 'ID',
                            enableHiding: true,
                            size: 0,
                          },
                          {
                            id: 'dateTime',
                            accessorKey: 'dateTime',
                            header: 'Date & Time',
                            enableSorting: true,
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <span className="text-sm text-muted-foreground whitespace-nowrap">{row.original.dateTime}</span>
                            ),
                          },
                          {
                            id: 'name',
                            accessorKey: 'name',
                            header: 'Badge',
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <div className="flex items-center gap-2">
                                {row.original.icon && (
                                  <img src={row.original.icon} alt={row.original.name} className="w-8 h-8 object-contain" />
                                )}
                                <span className="text-sm font-medium">{row.original.name}</span>
                              </div>
                            ),
                          },
                          {
                            id: 'event',
                            accessorKey: 'event',
                            header: 'Event',
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <Badge variant="outline" className="text-xs font-normal">
                                {row.original.event}
                              </Badge>
                            ),
                          },
                        ];

                        const levelColumns = [
                          {
                            id: 'id',
                            accessorKey: 'id',
                            header: 'ID',
                            enableHiding: true,
                            size: 0,
                          },
                          {
                            id: 'dateTime',
                            accessorKey: 'dateTime',
                            header: 'Date & Time',
                            enableSorting: true,
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <span className="text-sm text-muted-foreground whitespace-nowrap">{row.original.dateTime}</span>
                            ),
                          },
                          {
                            id: 'name',
                            accessorKey: 'name',
                            header: 'Level',
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                  </svg>
                                </div>
                                <span className="text-sm font-medium">{row.original.name}</span>
                              </div>
                            ),
                          },
                          {
                            id: 'pointsRequired',
                            accessorKey: 'pointsRequired',
                            header: 'Points Required',
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              // Pass-through: BE supplies `pointsRequired` per
                              // level row. The old code hardcoded thresholds
                              // (200/400/800/1200/1600) that drifted from any
                              // real level config.
                              <span className="text-sm font-semibold">
                                {row.original.pointsRequired
                                  ? `${row.original.pointsRequired} pts`
                                  : '-'}
                              </span>
                            ),
                          },
                          {
                            id: 'previousLevel',
                            accessorKey: 'previousLevel',
                            header: 'Previous Level',
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              // Pass-through: BE supplies `previousLevel` per row.
                              <Badge variant="outline" className="text-xs font-normal">
                                {row.original.previousLevel ?? '-'}
                              </Badge>
                            ),
                          },
                        ];

                        const pointsColumns = [
                          {
                            id: 'id',
                            accessorKey: 'id',
                            header: 'ID',
                            enableHiding: true,
                            size: 0,
                          },
                          {
                            id: 'dateTime',
                            accessorKey: 'dateTime',
                            header: 'Date & Time',
                            enableSorting: true,
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <span className="text-sm text-muted-foreground whitespace-nowrap">{row.original.dateTime}</span>
                            ),
                          },
                          {
                            id: 'amount',
                            accessorKey: 'amount',
                            header: 'Points Earned',
                            enableSorting: true,
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <span className="text-sm font-semibold text-green-600">{row.original.amount}</span>
                            ),
                          },
                          {
                            id: 'name',
                            accessorKey: 'name',
                            header: 'Activity',
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <Badge variant="outline" className="text-xs font-medium border-amber-200 bg-amber-50 text-amber-700">
                                {row.original.name}
                              </Badge>
                            ),
                          },
                          {
                            id: 'event',
                            accessorKey: 'event',
                            header: 'Event',
                            cell: ({ row }: { row: { original: LooseRow } }) => (
                              <span className="text-sm text-muted-foreground">{row.original.event}</span>
                            ),
                          },
                        ];

                        const activeColumns =
                          activityHistoryTab === 'badges' ? badgeColumns :
                          activityHistoryTab === 'levels' ? levelColumns :
                          pointsColumns;

                        const tablePrefix = `mgr-activity-history-${activityHistoryTab}`;

                        return (
                          <UnifiedDataTable
                              columns={activeColumns}
                              data={filteredEntries}
                              localStoragePrefix={tablePrefix}
                              enableSearch={false}
                              enableDensityToggle={false}
                              columnDefaults={{ visibility: { id: false } }}
                              maxHeight="260px"
                              noResultsText={`No ${activityHistoryTab} activity found`}
                              emptyState={{
                                mode: 'inline',
                                title: `No ${activityHistoryTab} activity found`,
                                description: 'Activity will appear here once events are recorded.',
                              }}
                            />
                        );
                      })()}
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Team Members Risk Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  {showSafest ? 'Low Risk Employees' : 'High Risk Employees'}
                  <Badge variant={showSafest ? 'secondary' : 'destructive'} className="ml-2">
                    {filteredRiskMembers.length}
                  </Badge>
                </h2>
                <div className="flex items-center gap-2">
                  <Label htmlFor="risk-toggle" className="text-sm text-muted-foreground">
                    High Risk
                  </Label>
                  <Switch
                    id="risk-toggle"
                    checked={showSafest}
                    onCheckedChange={(checked) => {
                      setShowSafest(checked);
                      setRiskTablePage(0);
                      setRiskLevelFilter('all');
                    }}
                  />
                  <Label htmlFor="risk-toggle" className="text-sm text-muted-foreground">
                    Low Risk
                  </Label>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={riskTableSearch}
                    onChange={(e) => {
                      setRiskTableSearch(e.target.value);
                      setRiskTablePage(0);
                    }}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                
                {/* Department Filter */}
                <Select 
                  value={departmentFilter} 
                  onValueChange={(value) => {
                    setDepartmentFilter(value);
                    setRiskTablePage(0);
                  }}
                >
                  <SelectTrigger className="w-[160px] h-9 text-sm text-foreground border-border">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {allDepartments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Clear Filters */}
                {(riskTableSearch || departmentFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRiskTableSearch('');
                      setDepartmentFilter('all');
                      setRiskTablePage(0);
                    }}
                    className="h-9 text-sm text-muted-foreground"
                  >
                    Clear filters
                  </Button>
                )}
              </div>

              {/* Table */}
              {paginatedRiskMembers.length > 0 ? (
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/80 transition-colors"
                          onClick={() => handleRiskTableSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            Employee
                            <ArrowUpDown className={`h-3.5 w-3.5 ${riskTableSort.key === 'name' ? 'text-foreground' : 'text-muted-foreground'}`} />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/80 transition-colors"
                          onClick={() => handleRiskTableSort('riskLevel')}
                        >
                          <div className="flex items-center gap-1">
                            Risk Level
                            <ArrowUpDown className={`h-3.5 w-3.5 ${riskTableSort.key === 'riskLevel' ? 'text-foreground' : 'text-muted-foreground'}`} />
                          </div>
                        </TableHead>
                        <TableHead>Phishing Performance</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/80 transition-colors"
                          onClick={() => handleRiskTableSort('trainingCompletion')}
                        >
                          <div className="flex items-center gap-1">
                            Training Status
                            <ArrowUpDown className={`h-3.5 w-3.5 ${riskTableSort.key === 'trainingCompletion' ? 'text-foreground' : 'text-muted-foreground'}`} />
                          </div>
                        </TableHead>
                        <TableHead>Risk Factors</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRiskMembers.map((member) => {
                        const riskFactors = getRiskFactors(member);
                        const trainingCompletion = member.trainingCompletion || 0;
                        const overdueRate = member.overdueRate || 0;
                        const reportRate = member.reportRate || 0;
                        const timesCompromised = member.timesCompromised || 0;
                        const visibleFactors = riskFactors.slice(0, 2);
                        const remainingFactors = riskFactors.length - 2;
                        
                        return (
                          <TableRow key={member.id} className="hover:bg-muted/30">
                            {/* Employee */}
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="space-y-1">
                                  <p className="font-medium text-sm text-foreground">{member.name}</p>
                                  <p className="text-xs text-muted-foreground">{member.email}</p>
                                  <Badge variant="outline" className="text-xs text-foreground">{member.department}</Badge>
                                </div>
                              </div>
                            </TableCell>
                            
                            {/* Risk Level */}
                            <TableCell>
                              <Badge className={getRiskLevelColor(member.riskLevel)}>
                                {member.riskLevel}
                              </Badge>
                            </TableCell>
                            
                            {/* Phishing Performance */}
                            <TableCell>
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">Compromised:</span>
                                  <span className={`text-sm font-semibold ${timesCompromised === 0 ? 'text-green-600 dark:text-green-400' : timesCompromised <= 1 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {timesCompromised}x
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">Report Rate:</span>
                                  <span className={`text-sm font-semibold ${reportRate >= 70 ? 'text-green-600 dark:text-green-400' : reportRate >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {reportRate}%
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            
                            {/* Training Status */}
                            <TableCell>
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">Completion:</span>
                                  <span className={`text-sm font-semibold ${trainingCompletion >= 80 ? 'text-green-600 dark:text-green-400' : trainingCompletion >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {trainingCompletion}%
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">Overdue:</span>
                                  <span className={`text-sm font-semibold ${overdueRate <= 10 ? 'text-green-600 dark:text-green-400' : overdueRate <= 30 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {overdueRate}%
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            
                            {/* Risk Factors */}
                            <TableCell>
                              <div className="flex flex-wrap items-center gap-1.5">
                                {riskFactors.length > 0 ? (
                                  <>
                                    {visibleFactors.map((factor, idx) => (
                                      <Badge 
                                        key={idx} 
                                        variant="outline"
                                        className={`text-xs ${factor.colorClass}`}
                                      >
                                        {factor.label}
                                      </Badge>
                                    ))}
                                    {remainingFactors > 0 && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant="outline" className="text-xs bg-muted">
                                            +{remainingFactors} more
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <div className="flex flex-col gap-1">
                                            {riskFactors.slice(2).map((factor, idx) => (
                                              <span key={idx} className="text-xs">{factor.label}</span>
                                            ))}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-xs text-green-600 font-medium">No issues</span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground border rounded-lg">
                  <p className="text-sm">No {showSafest ? 'low risk' : 'high risk'} team members found</p>
                  {(riskTableSearch || departmentFilter !== 'all') && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        setRiskTableSearch('');
                        setDepartmentFilter('all');
                      }}
                      className="mt-2"
                    >
                      Clear all filters
                    </Button>
                  )}
                </div>
              )}

              {/* Pagination */}
              {filteredRiskMembers.length > 0 && (
                <div className="flex items-center justify-between pt-2">
                  <div className="text-sm text-muted-foreground">
                    Showing {riskTablePage * riskTablePageSize + 1} - {Math.min((riskTablePage + 1) * riskTablePageSize, filteredRiskMembers.length)} of {filteredRiskMembers.length}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Page Size Selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Show</span>
                      <Select
                        value={`${riskTablePageSize}`}
                        onValueChange={(value) => {
                          setRiskTablePageSize(parseInt(value));
                          setRiskTablePage(0);
                        }}
                      >
                        <SelectTrigger className="w-[70px] h-8 text-sm text-foreground border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[5, 10, 20, 50].map(size => (
                            <SelectItem key={size} value={`${size}`}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Page Navigation */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label="Previous page"
                        className="h-8 w-8"
                        onClick={() => setRiskTablePage(p => Math.max(0, p - 1))}
                        disabled={riskTablePage === 0}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-1 px-2">
                        <span className="text-sm font-medium">{riskTablePage + 1}</span>
                        <span className="text-sm text-muted-foreground">of</span>
                        <span className="text-sm font-medium">{riskTablePageCount || 1}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label="Next page"
                        className="h-8 w-8"
                        onClick={() => setRiskTablePage(p => Math.min(riskTablePageCount - 1, p + 1))}
                        disabled={riskTablePage >= riskTablePageCount - 1}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Go to Page */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Go to</span>
                      <Input
                        type="number"
                        min={1}
                        max={riskTablePageCount}
                        className="h-8 w-14 text-center text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const value = parseInt((e.target as HTMLInputElement).value);
                            if (value >= 1 && value <= riskTablePageCount) {
                              setRiskTablePage(value - 1);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Monthly Team Recap launcher (ticket 846) — a persistent card so
                admins in view-as (previewMode) and managers can always open the
                recap on demand, rather than relying on the once-per-week
                auto-open effect that is intentionally suppressed in previewMode. */}
            {hasWeeklyRecap && (
              <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
                <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">Monthly Team Recap</h3>
                      <p className="text-sm text-muted-foreground">
                        A slide-by-slide summary of your team&apos;s gamification, phishing, training and announcement performance.
                      </p>
                    </div>
                  </div>
                  <Button
                    className="shrink-0"
                    onClick={() => {
                      setRecapSlide(0);
                      setShowWeeklyRecap(true);
                    }}
                  >
                    <PartyPopper className="mr-2 h-4 w-4" />
                    View Recap
                  </Button>
                </CardContent>
              </Card>
            )}
            </TabsContent>

            {/* My Team Tab Content */}
            <TabsContent value="myteam" className="m-0 space-y-6">
              {/* Manager Info Bar */}
              <div className="flex items-center justify-between py-4 px-6 bg-muted/30 rounded-lg border">
                <div className="flex items-center gap-8">
                  {/* Back Button when viewing as someone else */}
                  {viewAsManager && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Back to my team"
                      onClick={handleBackToMyTeam}
                      className="shrink-0 h-8 w-8"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                  )}

                  {/* Manager Avatar and Info */}
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={myTeamCurrentManager.avatar} alt={myTeamCurrentManager.name} />
                      <AvatarFallback>
                        {myTeamCurrentManager.name.split(' ').map((n: string) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{myTeamCurrentManager.name}</p>
                        {viewAsManager && (
                          <Badge variant="secondary" className="text-xs">Viewing As</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                    </div>
                  </div>
                  
                  {/* Designation */}
                  <div>
                    <p className="text-xs text-muted-foreground">Designation</p>
                    <p className="text-sm text-foreground">{myTeamCurrentManager.designation}</p>
                  </div>

                  {/* Reports To */}
                  <div>
                    <p className="text-xs text-muted-foreground">Reports To</p>
                    <p className="text-sm text-foreground">{getMyTeamCurrentManagersManager().name}</p>
                  </div>
                </div>

                {/* Team Stats on the right - clickable filters */}
                <div className="flex items-center gap-2">
                  {[
                    { key: 'direct' as const, count: myTeamFilterCounts.direct, label: 'Direct Members' },
                    { key: 'indirect' as const, count: myTeamFilterCounts.indirect, label: 'Indirect Members' },
                    { key: 'myteam' as const, count: myTeamFilterCounts.myteam, label: 'Managers in my team' },
                    { key: 'all' as const, count: myTeamFilterCounts.all, label: 'All Members' },
                  ].map((stat) => (
                    <button
                      key={stat.key}
                      onClick={() => { setMyTeamFilter(stat.key); setMyTeamCurrentPage(1); }}
                      className={`text-center px-4 py-2 rounded-lg transition-all cursor-pointer ${
                        myTeamFilter === stat.key
                          ? 'bg-foreground text-background shadow-sm'
                          : 'hover:bg-muted active:bg-muted/80'
                      }`}
                    >
                      <p className={`text-xl font-bold ${myTeamFilter === stat.key ? 'text-background' : 'text-foreground'}`}>{stat.count}</p>
                      <p className={`text-xs ${myTeamFilter === stat.key ? 'text-background/70' : 'text-muted-foreground'}`}>{stat.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Table Section */}
              <Tabs value={myTeamActiveTab} onValueChange={setMyTeamActiveTab} className="space-y-4">

                {/* Table Card with Tabs */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <TabsList className="h-auto p-0 bg-transparent gap-1 w-fit">
                        {showMyTeamLeaderboard && <TabsTrigger value="leaderboard" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">Leaderboard</TabsTrigger>}
                        {showMyTeamRiskScore && <TabsTrigger value="risk-score" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">Risk Score</TabsTrigger>}
                        {showMyTeamPhishing && <TabsTrigger value="phishing" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">Phishing</TabsTrigger>}
                        {showMyTeamTraining && <TabsTrigger value="training" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">Training</TabsTrigger>}
                        {showMyTeamAnnouncement && <TabsTrigger value="announcement" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">Announcement</TabsTrigger>}
                      </TabsList>
                    </div>
                    {/* Search and Filters - moved inside card */}
                    <div className="flex items-center gap-4 mt-6 pt-3">
                      <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by Name / Email / Designation / Department"
                          value={myTeamSearchQuery}
                          onChange={(e) => { setMyTeamSearchQuery(e.target.value); setMyTeamCurrentPage(1); }}
                          className="pl-10"
                        />
                      </div>
                      <Select
                        value={myTeamDepartmentFilter}
                        onValueChange={(value) => { setMyTeamDepartmentFilter(value); setMyTeamCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-40 text-foreground border-border">
                          <SelectValue placeholder="All Departments" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Departments</SelectItem>
                          {myTeamDepartments.map((dept) => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={myTeamSort}
                        onValueChange={(value) => { setMyTeamSort(value as 'name' | 'lastActivity' | 'points'); setMyTeamCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-40 text-foreground border-border" aria-label="Sort by">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lastActivity">Last Activity</SelectItem>
                          <SelectItem value="name">Name (A–Z)</SelectItem>
                          <SelectItem value="points">Points (High–Low)</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* Context-aware filter drawers */}
                      {myTeamActiveTab === 'phishing' && (
                        <div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => setMyTeamPhishingDrawerOpen(true)}
                          >
                            <Filter className="h-4 w-4" />
                            Filter Campaigns
                            {myTeamSelectedPhishingCampaigns.length < myTeamPhishingCampaigns.length && (
                              <Badge variant="secondary" className="ml-1 px-1.5 py-0">
                                {myTeamSelectedPhishingCampaigns.length}
                              </Badge>
                            )}
                          </Button>
                          <AdvancedFilterDrawer
                            open={myTeamPhishingDrawerOpen}
                            onOpenChange={setMyTeamPhishingDrawerOpen}
                            entityType="campaign"
                            entityLabel="Campaigns"
                            entityLabelSingular="Campaign"
                            items={myTeamPhishingCampaigns as unknown as FilterableEntity[]}
                            selectedIds={myTeamSelectedPhishingCampaigns}
                            onApply={setMyTeamSelectedPhishingCampaigns}
                          />
                        </div>
                      )}
                      {myTeamActiveTab === 'training' && (
                        <div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => setMyTeamTrainingDrawerOpen(true)}
                          >
                            <Filter className="h-4 w-4" />
                            Filter Training
                            {myTeamSelectedTrainingCourses.length < myTeamTrainingCourses.length && (
                              <Badge variant="secondary" className="ml-1 px-1.5 py-0">
                                {myTeamSelectedTrainingCourses.length}
                              </Badge>
                            )}
                          </Button>
                          <AdvancedFilterDrawer
                            open={myTeamTrainingDrawerOpen}
                            onOpenChange={setMyTeamTrainingDrawerOpen}
                            entityType="training"
                            entityLabel="Training Courses"
                            entityLabelSingular="Training Course"
                            items={myTeamTrainingCourses as unknown as FilterableEntity[]}
                            selectedIds={myTeamSelectedTrainingCourses}
                            onApply={setMyTeamSelectedTrainingCourses}
                          />
                        </div>
                      )}
                      {myTeamActiveTab === 'announcement' && (
                        <div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => setMyTeamAnnouncementDrawerOpen(true)}
                          >
                            <Filter className="h-4 w-4" />
                            Filter Announcements
                            {myTeamSelectedAnnouncementCampaigns.length < myTeamAnnouncementCampaigns.length && (
                              <Badge variant="secondary" className="ml-1 px-1.5 py-0">
                                {myTeamSelectedAnnouncementCampaigns.length}
                              </Badge>
                            )}
                          </Button>
                          <AdvancedFilterDrawer
                            open={myTeamAnnouncementDrawerOpen}
                            onOpenChange={setMyTeamAnnouncementDrawerOpen}
                            entityType="announcement"
                            entityLabel="Announcements"
                            entityLabelSingular="Announcement"
                            items={myTeamAnnouncementCampaigns as unknown as FilterableEntity[]}
                            selectedIds={myTeamSelectedAnnouncementCampaigns}
                            onApply={setMyTeamSelectedAnnouncementCampaigns}
                          />
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  {/* Leaderboard Tab */}
                  {showMyTeamLeaderboard && (
                  <TabsContent value="leaderboard" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:pl-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold pl-4">Employee</TableHead>
                              {myTeamFilter === 'myteam' && <TableHead className="font-semibold text-center">View As</TableHead>}
                              <TableHead className="font-semibold text-center">Members in Team</TableHead>
                              <TableHead className="font-semibold text-center">Points</TableHead>
                              <TableHead className="font-semibold text-center">Rank in Team</TableHead>
                              <TableHead className="font-semibold text-center">Rank in Department</TableHead>
                              <TableHead className="font-semibold text-center">Rank in Org</TableHead>
                              <TableHead className="font-semibold">Department</TableHead>
                              <TableHead className="font-semibold">Designation</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">Hierarchy</TableHead>}
                              <TableHead className="font-semibold">Manager</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleMyTeamRowClick(member.id)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                {myTeamFilter === 'myteam' && (
                                  <TableCell className="text-center">
                                    {member.isManager && member.membersInTeam > 0 ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" aria-label={`View as ${member.name}`} className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleViewAs(member); }}>
                                              <Eye className="h-4 w-4 text-muted-foreground" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent><p>View as {member.name}</p></TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : <span className="text-muted-foreground">-</span>}
                                  </TableCell>
                                )}
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell className="text-center font-semibold">{member.totalPoints}</TableCell>
                                <TableCell className="text-center font-semibold">{member.rank}</TableCell>
                                <TableCell className="text-center font-semibold">{member.rankInDepartment}</TableCell>
                                <TableCell className="text-center font-semibold">{member.rankInOrg}</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                <TableCell><p className="text-sm">{member.designation}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Risk Score Tab */}
                  {showMyTeamRiskScore && (
                  <TabsContent value="risk-score" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:pl-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold pl-4">Employee</TableHead>
                              <TableHead className="font-semibold text-center">Members in Team</TableHead>
                              <TableHead className="font-semibold">Department</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">Hierarchy</TableHead>}
                              <TableHead className="font-semibold">Risk Score</TableHead>
                              <TableHead className="font-semibold">Manager</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleMyTeamRowClick(member.id)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>{getRiskScoreBadge(member.riskLevel, member.riskScore)}</TableCell>
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Phishing Tab */}
                  {showMyTeamPhishing && (
                  <TabsContent value="phishing" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:pl-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold pl-4">Employee</TableHead>
                              <TableHead className="font-semibold text-center">Members in Team</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">Hierarchy</TableHead>}
                              <TableHead className="font-semibold text-center">Sent</TableHead>
                              <TableHead className="font-semibold text-center">Opened</TableHead>
                              <TableHead className="font-semibold text-center">Clicked</TableHead>
                              <TableHead className="font-semibold text-center">Compromised</TableHead>
                              <TableHead className="font-semibold text-center">Reported</TableHead>
                              <TableHead className="font-semibold text-center">Trained</TableHead>
                              <TableHead className="font-semibold text-center">Evaluated</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleMyTeamRowClick(member.id)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell className="text-center font-semibold">{member.phishing.sent}</TableCell>
                                <TableCell className="text-center">{member.phishing.opened} | {member.phishing.openedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.clicked} | {member.phishing.clickedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.compromised} | {member.phishing.compromisedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.reported} | {member.phishing.reportedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.trained} | {member.phishing.trainedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.evaluated} | {member.phishing.evaluatedRate}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Training Tab */}
                  {showMyTeamTraining && (
                  <TabsContent value="training" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:pl-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold pl-4">Employee</TableHead>
                              <TableHead className="font-semibold text-center">Members in Team</TableHead>
                              <TableHead className="font-semibold text-center">Assigned</TableHead>
                              <TableHead className="font-semibold text-center">Completed</TableHead>
                              <TableHead className="font-semibold text-center">In Progress</TableHead>
                              <TableHead className="font-semibold text-center">Trained</TableHead>
                              <TableHead className="font-semibold text-center">Evaluated</TableHead>
                              <TableHead className="font-semibold text-center">Expired</TableHead>
                              <TableHead className="font-semibold">Department</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">Hierarchy</TableHead>}
                              <TableHead className="font-semibold">Manager</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleMyTeamRowClick(member.id)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell className="text-center font-semibold">{member.training.assigned}</TableCell>
                                <TableCell className="text-center">{member.training.completed} | {member.training.completedRate}%</TableCell>
                                <TableCell className="text-center">{member.training.inProgress} | {member.training.inProgressRate}%</TableCell>
                                <TableCell className="text-center">{member.training.trained} | {member.training.trainedRate}%</TableCell>
                                <TableCell className="text-center">{member.training.evaluated} | {member.training.evaluatedRate}%</TableCell>
                                <TableCell className="text-center">{member.training.expired} | {member.training.expiredRate}%</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Announcement Tab */}
                  {showMyTeamAnnouncement && (
                  <TabsContent value="announcement" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:pl-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold pl-4">Employee</TableHead>
                              <TableHead className="font-semibold text-center">Members in Team</TableHead>
                              <TableHead className="font-semibold text-center">Sent</TableHead>
                              <TableHead className="font-semibold text-center">Opened</TableHead>
                              <TableHead className="font-semibold text-center">Acknowledged</TableHead>
                              <TableHead className="font-semibold text-center">Reported</TableHead>
                              <TableHead className="font-semibold text-center">Trained</TableHead>
                              <TableHead className="font-semibold text-center">Evaluated</TableHead>
                              <TableHead className="font-semibold">Department</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">Hierarchy</TableHead>}
                              <TableHead className="font-semibold">Manager</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleMyTeamRowClick(member.id)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell className="text-center font-semibold">{member.announcement.sent}</TableCell>
                                <TableCell className="text-center">{member.announcement.opened} | {member.announcement.openedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.acknowledged} | {member.announcement.acknowledgedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.reported} | {member.announcement.reportedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.trained} | {member.announcement.trainedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.evaluated} | {member.announcement.evaluatedRate}%</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}
                </Card>
              </Tabs>

              {/* Pagination for My Team */}
              {myTeamActiveTabMembers.length > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {(myTeamCurrentPage - 1) * myTeamItemsPerPage + 1} to{' '}
                    {Math.min(myTeamCurrentPage * myTeamItemsPerPage, myTeamActiveTabMembers.length)} of{' '}
                    {myTeamActiveTabMembers.length} members
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Rows per page</span>
                      <Select
                        value={String(myTeamItemsPerPage)}
                        onValueChange={(value) => { setMyTeamItemsPerPage(Number(value)); setMyTeamCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-[72px] h-8 text-foreground border-border" aria-label="Rows per page">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[10, 25, 50, 100].map((size) => (
                            <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" aria-label="Previous page" onClick={() => setMyTeamCurrentPage((p) => Math.max(1, p - 1))} disabled={myTeamCurrentPage === 1}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm px-2">{myTeamCurrentPage} of {myTeamTotalPages}</span>
                      <Button variant="outline" size="icon" aria-label="Next page" onClick={() => setMyTeamCurrentPage((p) => Math.min(myTeamTotalPages, p + 1))} disabled={myTeamCurrentPage >= myTeamTotalPages}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
            </Tabs>
          </div>

          {/* Team Hierarchy Sheet (Side Drawer)
              <HierarchyTree> is scoped to the VIEWED manager's real reportee
              sub-tree: rooted at `myTeamManagerProfile` and assembled from the
              flat `myTeamAllMembers` list (each linked via `reportsTo`). This
              shows only the selected manager's sub-tree — not the whole org.
              When no real manager context exists (admin-builder preview with no
              BE data — `myTeamManagerProfile.id` is empty), HierarchyTree falls
              back to its synthetic mock org for layout review only. */}
          <Sheet open={isHierarchyModalOpen} onOpenChange={setIsHierarchyModalOpen}>
            <SheetContent side="right" className="w-[60vw] sm:max-w-[60vw] p-0">
              <SheetHeader className="px-6 py-4 border-b">
                <SheetTitle>Organization Hierarchy</SheetTitle>
              </SheetHeader>
              <div className="h-[calc(100vh-80px)]">
                {myTeamManagerProfile.id ? (
                  <HierarchyTree
                    rootMember={{
                      id: myTeamManagerProfile.id,
                      name: myTeamManagerProfile.name,
                      email: myTeamManagerProfile.email,
                      avatar: myTeamManagerProfile.avatar,
                      department: myTeamManagerProfile.department,
                      designation: myTeamManagerProfile.designation,
                    }}
                    members={myTeamAllMembers.map((m) => ({
                      id: m.id,
                      name: m.name,
                      email: m.email,
                      avatar: m.avatar,
                      department: m.department,
                      designation: m.designation,
                      isManager: m.isManager,
                      reportsTo: m.reportsTo,
                    }))}
                  />
                ) : previewMode ? (
                  // Admin-builder preview with no real manager context: show the
                  // synthetic mock org for layout review only. The mock never
                  // reaches a live employee (gated by `previewMode`).
                  <HierarchyTree />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      Team hierarchy view is not yet available for your workspace.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Direct reports: {dashboardData?.directReportCount ?? 0}
                      {' · '}
                      Indirect reports: {dashboardData?.indirectReportCount ?? 0}
                    </p>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* Employee List Modal */}
          <Dialog open={isEmployeeListModalOpen} onOpenChange={(open) => {
            setIsEmployeeListModalOpen(open);
            if (!open) setCurrentPage(1);
          }}>
            <DialogContent className="sm:max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {employeeListCategory === 'all' && 'All Team Members'}
                  {employeeListCategory === 'direct' && 'Direct Reports'}
                  {employeeListCategory === 'indirect' && 'Indirect Reports'}
                  {employeeListCategory === 'managers' && 'Managers'}
                  {' '}({getEmployeesForCategory().length})
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Paginated list of team members in the selected category
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                {(() => {
                  const employees = getEmployeesForCategory();
                  const totalPages = Math.ceil(employees.length / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const paginatedEmployees = employees.slice(startIndex, startIndex + itemsPerPage);

                  return (
                    <>
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead>Points</TableHead>
                            <TableHead>Level</TableHead>
                            <TableHead>Badges</TableHead>
                            <TableHead>Risk</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedEmployees.map((employee) => {
                            const levelInfo = getLevelInfo(employee.totalPoints);
                            return (
                              <TableRow 
                                key={employee.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => router.push(`/user-portal/${employee.id}`)}
                              >
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarFallback className={`${getAvatarColor(employee.name)} text-white text-sm font-semibold`}>
                                        {getInitials(employee.name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium text-sm">{employee.name}</p>
                                      <p className="text-xs text-muted-foreground">{employee.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="font-medium">{(employee.totalPoints ?? 0).toLocaleString()}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <img 
                                      src={levelInfo.badge} 
                                      alt={levelInfo.name}
                                      className="w-8 h-8 object-contain"
                                    />
                                    <span className="text-sm font-medium">{levelInfo.name}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="flex -space-x-1">
                                      {badgeImages.slice(0, Math.min(employee.badgeCount, 3)).map((src, i) => (
                                        <img 
                                          key={i} 
                                          src={src} 
                                          alt={`Badge ${i + 1}`} 
                                          className="w-6 h-6 rounded-full ring-1 ring-white object-cover" 
                                        />
                                      ))}
                                    </div>
                                    <span className="text-sm text-muted-foreground">
                                      {employee.badgeCount}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge className={getRiskLevelColor(employee.riskLevel)}>
                                    {employee.riskLevel}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 px-2">
                          <p className="text-sm text-muted-foreground">
                            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, employees.length)} of {employees.length} members
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Previous
                            </Button>
                            <div className="flex items-center gap-1">
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <Button
                                  key={page}
                                  variant={currentPage === page ? 'default' : 'outline'}
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </Button>
                              ))}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={currentPage === totalPages}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </DialogContent>
          </Dialog>

          {/* Weekly Recap Modal — once-per-week, dismissible (gated by
              localStorage key hf-portal:weekly-recap-dismissed:<iso-week>) */}
          <Dialog
            open={showWeeklyRecap}
            onOpenChange={(open) => {
              if (!open) handleDismissWeeklyRecap();
            }}
          >
            <DialogContent className="w-[70vw] !max-w-[70vw] h-[70vh] p-0 overflow-hidden border border-border bg-background rounded-xl shadow-2xl [&>button]:hidden">
              <DialogTitle className="sr-only">Monthly Team Recap</DialogTitle>
              <DialogDescription className="sr-only">
                Weekly summary of team security awareness and phishing campaign performance
              </DialogDescription>
              {weeklyRecapData ? (
              <div className="relative overflow-hidden h-full flex flex-col">
                {/* Confetti Animation for Positive Gamification */}
                {recapSlide === 0 && weeklyRecapData.gamification.isPositive && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
                    {[...Array(50)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute animate-confetti"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: '-20px',
                          animationDelay: `${Math.random() * 3}s`,
                          animationDuration: `${3 + Math.random() * 2}s`,
                        }}
                      >
                        <div
                          className="w-3 h-3 rotate-45"
                          style={{
                            backgroundColor: ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#06b6d4'][Math.floor(Math.random() * 6)],
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Sad Face Rain for Poor Campaign */}
                {recapSlide === 1 && !weeklyRecapData.campaign.isPositive && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute animate-fall text-2xl opacity-70"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: '-40px',
                          animationDelay: `${Math.random() * 4}s`,
                          animationDuration: `${4 + Math.random() * 3}s`,
                        }}
                      >
                        😢
                      </div>
                    ))}
                  </div>
                )}

                {/* Close Button — dismisses for the current ISO week */}
                <button
                  onClick={handleDismissWeeklyRecap}
                  className="absolute top-4 right-4 z-20 p-2 rounded-full bg-background/80 hover:bg-background shadow-lg transition-all hover:scale-110"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Header */}
                <div className="bg-muted/20 p-5 pb-4 border-b border-border">
                  <h2 className="text-xl font-bold text-foreground">Monthly Team Recap</h2>
                </div>

                {/* Carousel Content */}
                <div className="p-6 pt-4 flex-1 overflow-y-auto">
                  {/* Slide 0: Gamification */}
                  {recapSlide === 0 && (
                    <div className="animate-fadeIn">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Gamification</Badge>
                          </div>
                          <p className="text-lg font-semibold">Team Performance</p>
                          <p className="text-xs text-muted-foreground">November 2025 • {allTeamMembers.length} team members</p>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="page-heading text-foreground">+{weeklyRecapData.gamification.pointsGained}</span>
                          <span className="text-sm text-muted-foreground">points</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        {/* Left Column - Summary */}
                        <div className="space-y-4">
                          {/* Points Summary */}
                          <div className="bg-muted/10 rounded-lg p-4 border">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-muted-foreground font-medium mb-1">Previous Total</p>
                                <p className="text-xl font-bold text-foreground">{weeklyRecapData.gamification.previousPoints}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground font-medium mb-1">New Total</p>
                                <p className="text-xl font-bold text-foreground">{weeklyRecapData.gamification.previousPoints + weeklyRecapData.gamification.pointsGained}</p>
                              </div>
                            </div>
                          </div>

                          {/* Badges Card */}
                          <div className="bg-muted/10 rounded-lg p-4 border">
                            <p className="text-xs text-muted-foreground font-medium mb-3">Badges Earned</p>
                            <div className="space-y-2">
                              {weeklyRecapData.gamification.newBadges.map((badge: LooseRow, idx: number) => (
                                <div key={idx} className="flex items-center gap-3">
                                  <img src={badge.icon} alt={badge.name} className="w-8 h-8" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{badge.name}</p>
                                    <p className="text-xs text-muted-foreground">{badge.count} employee{badge.count > 1 ? 's' : ''}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Level Ups Card */}
                          {weeklyRecapData.gamification.levelUps.length > 0 && (
                            <div className="bg-muted/10 rounded-lg p-4 border">
                              <p className="text-xs text-muted-foreground font-medium mb-3">Level Ups</p>
                              <div className="space-y-2">
                                {weeklyRecapData.gamification.levelUps.map((person: LooseRow, idx: number) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <span className="text-sm font-medium">{person.name}</span>
                                    <span className="text-xs text-muted-foreground">{person.from} → {person.to}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right Column - Employee Insights */}
                        <div className="bg-muted/10 rounded-lg p-4 border h-fit">
                          <p className="text-xs text-muted-foreground font-medium mb-3">Employee Insights</p>
                          
                          {/* Top Performers */}
                          <div className="mb-4">
                            <p className="text-sm font-semibold mb-2">Top Performers</p>
                            <div className="space-y-2">
                              {[...allTeamMembers].sort((a, b) => b.totalPoints - a.totalPoints).slice(0, 3).map((emp, idx) => (
                                <div key={emp.id} className="flex items-center justify-between bg-background rounded-lg p-2 border">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-muted-foreground w-4">{idx + 1}</span>
                                    <Avatar className="h-8 w-8">
                                      <AvatarFallback className={`${getAvatarColor(emp.name)} text-white text-xs font-semibold`}>
                                        {getInitials(emp.name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm font-medium">{emp.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>{emp.totalPoints} pts</span>
                                    <span>•</span>
                                    <span>{emp.badgeCount} badges</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Need Attention */}
                          <div>
                            <p className="text-sm font-semibold mb-2">Need Attention</p>
                            <div className="space-y-2">
                              {[...allTeamMembers].sort((a, b) => a.totalPoints - b.totalPoints).slice(0, 2).map((emp) => (
                                <div key={emp.id} className="flex items-center justify-between bg-background rounded-lg p-2 border">
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-8 w-8">
                                      <AvatarFallback className={`${getAvatarColor(emp.name)} text-white text-xs font-semibold`}>
                                        {getInitials(emp.name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm font-medium">{emp.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>{emp.totalPoints} pts</span>
                                    <span>•</span>
                                    <span>{emp.badgeCount} badges</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Slide 1: Campaign Results */}
                  {recapSlide === 1 && (
                    <div className="animate-fadeIn">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Phishing</Badge>
                          </div>
                          <p className="text-lg font-semibold">{weeklyRecapData.campaign.campaignName}</p>
                          <p className="text-xs text-muted-foreground">{weeklyRecapData.campaign.runDate} • {weeklyRecapData.campaign.totalParticipants} members</p>
                        </div>
                      </div>

                      {/* SOCRATE Funnel - Horizontal Flow */}
                      <div className="bg-muted/10 rounded-lg p-4 border mb-4">
                        <p className="text-xs text-muted-foreground font-medium mb-4">Campaign Funnel (S-O-C-C-R-T-E)</p>
                        <div className="flex items-center justify-between">
                          {/* Sent */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Sent</div>
                            <div className="text-sm font-bold text-center">{weeklyRecapData.campaign.stats.sent}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Opened */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-yellow-50 flex items-center justify-center">
                                <Eye className="w-3.5 h-3.5 text-yellow-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Opened</div>
                            <div className="text-sm font-bold text-center">{weeklyRecapData.campaign.stats.opened} <span className="text-xs font-normal text-muted-foreground">{weeklyRecapData.campaign.stats.openedRate}%</span></div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Clicked */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-orange-50 flex items-center justify-center">
                                <MousePointer className="w-3.5 h-3.5 text-orange-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Clicked</div>
                            <div className="text-sm font-bold text-center">{weeklyRecapData.campaign.stats.clicked} <span className="text-xs font-normal text-muted-foreground">{weeklyRecapData.campaign.stats.clickedRate}%</span></div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Compromised */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Compromised</div>
                            <div className="text-sm font-bold text-center text-red-600">{weeklyRecapData.campaign.stats.compromised} <span className="text-xs font-normal">{weeklyRecapData.campaign.stats.compromisedRate}%</span></div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Reported */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
                                <Shield className="w-3.5 h-3.5 text-green-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Reported</div>
                            <div className="text-sm font-bold text-center text-green-600">{weeklyRecapData.campaign.stats.reported} <span className="text-xs font-normal">{weeklyRecapData.campaign.stats.reportedRate}%</span></div>
                          </div>
                        </div>
                      </div>

                      {/* Employee Insights - Two Column */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Compromised */}
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            Compromised Employees
                          </p>
                          <div className="space-y-2">
                            {weeklyRecapData.campaign.compromisedEmployees.map((emp: LooseRow, idx: number) => (
                              <div key={idx} className="bg-background rounded-lg p-2 border text-sm">{emp.name}</div>
                            ))}
                            {allTeamMembers.filter(m => (m.timesCompromised || 0) >= 2).slice(0, 1).map((emp) => (
                              <div key={emp.id} className="flex items-center justify-between bg-background rounded-lg p-2 border">
                                <span className="text-sm">{emp.name}</span>
                                <span className="text-xs text-red-600">{emp.timesCompromised}x</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Top Reporters */}
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            Top Reporters
                          </p>
                          <div className="space-y-2">
                            {[...allTeamMembers].sort((a, b) => (b.reportRate || 0) - (a.reportRate || 0)).slice(0, 3).map((emp) => (
                              <div key={emp.id} className="flex items-center justify-between bg-background rounded-lg p-2 border">
                                <span className="text-sm">{emp.name}</span>
                                <span className="text-xs text-green-600">{emp.reportRate}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Slide 2: Training Progress */}
                  {recapSlide === 2 && (
                    <div className="animate-fadeIn">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">Training</Badge>
                          </div>
                          <p className="text-lg font-semibold">{trainingCourses.length} Active Courses</p>
                          <p className="text-xs text-muted-foreground">November 2025 • {weeklyRecapData.training.totalAssigned} assigned</p>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="page-heading text-foreground">{weeklyRecapData.training.completionRate}%</span>
                          <span className="text-sm text-green-600 font-medium">+{weeklyRecapData.training.completionRate - weeklyRecapData.training.previousRate}%</span>
                        </div>
                      </div>

                      {/* Training Funnel - Horizontal Flow */}
                      <div className="bg-muted/10 rounded-lg p-4 border mb-4">
                        <p className="text-xs text-muted-foreground font-medium mb-4">Training Funnel</p>
                        <div className="flex items-center justify-between">
                          {/* Assigned */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Assigned</div>
                            <div className="text-sm font-bold text-center">{weeklyRecapData.training.totalAssigned}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* In Progress */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-yellow-50 flex items-center justify-center">
                                <Clock className="w-3.5 h-3.5 text-yellow-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">In Progress</div>
                            <div className="text-sm font-bold text-center">{trainingCourses.reduce((sum, c) => sum + c.inProgress, 0)}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Completed */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Completed</div>
                            <div className="text-sm font-bold text-center text-green-600">{weeklyRecapData.training.newCompletions}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Not Started */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-orange-50 flex items-center justify-center">
                                <Circle className="w-3.5 h-3.5 text-orange-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Not Started</div>
                            <div className="text-sm font-bold text-center text-orange-600">{trainingCourses.reduce((sum, c) => sum + c.notStarted, 0)}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Overdue */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Overdue</div>
                            <div className="text-sm font-bold text-center text-red-600">{trainingCourses.reduce((sum, c) => sum + c.overdue, 0)}</div>
                          </div>
                        </div>
                      </div>

                      {/* Employee Insights - Two Column */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Completed This Month */}
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            Completed This Month
                          </p>
                          <div className="space-y-2">
                            {weeklyRecapData.training.completedThisWeek.map((person: LooseRow, idx: number) => (
                              <div key={idx} className="bg-background rounded-lg p-2 border">
                                <p className="text-sm font-medium">{person.name}</p>
                                <p className="text-xs text-muted-foreground">{person.course}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Pending/Overdue */}
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                            Pending / Overdue
                          </p>
                          <div className="space-y-2">
                            {allTeamMembers.filter(m => (m.trainingCompletion || 0) < 50 || (m.overdueRate || 0) > 30).slice(0, 3).map((emp) => (
                              <div key={emp.id} className="flex items-center justify-between bg-background rounded-lg p-2 border">
                                <span className="text-sm">{emp.name}</span>
                                <span className="text-xs text-orange-600">{emp.trainingCompletion}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Slide 3: Announcements */}
                  {recapSlide === 3 && (
                    <div className="animate-fadeIn">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 text-xs">Announcement</Badge>
                          </div>
                          <p className="text-lg font-semibold">{announcementCampaigns.length} Announcements</p>
                          <p className="text-xs text-muted-foreground">November 2025 • {weeklyRecapData.announcement.totalSent} sent</p>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="page-heading text-foreground">{weeklyRecapData.announcement.acknowledgedRate}%</span>
                          <span className="text-sm text-muted-foreground">acknowledged</span>
                        </div>
                      </div>

                      {/* SOCATE Funnel - Horizontal Flow */}
                      <div className="bg-muted/10 rounded-lg p-4 border mb-4">
                        <p className="text-xs text-muted-foreground font-medium mb-4">Announcement Funnel (S-O-C-A-T)</p>
                        <div className="flex items-center justify-between">
                          {/* Sent */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Sent</div>
                            <div className="text-sm font-bold text-center">{announcementCampaigns.reduce((sum, c) => sum + c.sent, 0)}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Opened */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-yellow-50 flex items-center justify-center">
                                <Eye className="w-3.5 h-3.5 text-yellow-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Opened</div>
                            <div className="text-sm font-bold text-center">{announcementCampaigns.reduce((sum, c) => sum + c.opened, 0)}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Clicked */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-orange-50 flex items-center justify-center">
                                <MousePointer className="w-3.5 h-3.5 text-orange-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Clicked</div>
                            <div className="text-sm font-bold text-center">{Math.round(announcementCampaigns.reduce((sum, c) => sum + c.opened, 0) * 0.6)}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Acknowledged */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Acknowledged</div>
                            <div className="text-sm font-bold text-center text-green-600">{announcementCampaigns.reduce((sum, c) => sum + c.acknowledged, 0)}</div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {/* Trained */}
                          <div className="flex flex-col items-center flex-1">
                            <div className="flex items-center justify-center mb-1 gap-1">
                              <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center">
                                <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                              </div>
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center">Trained</div>
                            <div className="text-sm font-bold text-center text-blue-600">{announcementCampaigns.reduce((sum, c) => sum + c.trained, 0)}</div>
                          </div>
                        </div>
                      </div>

                      {/* Employee Insights - Two Column */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Fully Acknowledged */}
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            Fully Acknowledged
                          </p>
                          <div className="space-y-2">
                            {allTeamMembers.filter(m => m.riskLevel === 'Low').slice(0, 3).map((emp) => (
                              <div key={emp.id} className="bg-background rounded-lg p-2 border text-sm">{emp.name}</div>
                            ))}
                          </div>
                        </div>

                        {/* Pending Acknowledgments */}
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                            Pending Acknowledgments
                          </p>
                          <div className="space-y-2">
                            {weeklyRecapData.announcement.unacknowledged.map((person: LooseRow, idx: number) => (
                              <div key={idx} className="flex items-center justify-between bg-background rounded-lg p-2 border">
                                <span className="text-sm">{person.name}</span>
                                <span className="text-xs text-orange-600">{person.pending} pending</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="px-6 py-3 border-t border-border bg-muted/10">
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRecapSlide((prev) => (prev - 1 + totalRecapSlides) % totalRecapSlides)}
                      className="gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>

                    {/* Carousel Dots */}
                    <div className="flex items-center gap-2">
                      {[0, 1, 2, 3].map((idx) => (
                        <button
                          key={idx}
                          onClick={() => setRecapSlide(idx)}
                          className={`h-2 rounded-full transition-all ${
                            recapSlide === idx ? 'bg-primary w-8' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50 w-2'
                          }`}
                        />
                      ))}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRecapSlide((prev) => (prev + 1) % totalRecapSlides)}
                      className="gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  No recap data available yet.
                </div>
              )}

{/* CSS animations removed for now */}
            </DialogContent>
          </Dialog>
    </TooltipProvider>
  );
}
